import React, { useState, useEffect, useMemo } from 'react';
import './OperationDashboard.css';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Label, AreaChart, Area, BarChart, Bar, ComposedChart, PieChart, Pie, Cell, Legend } from 'recharts';
import MarkdownEditor from '../components/common/MarkdownEditor.jsx';
import adminApi from '../utils/adminRequest';
import toast from 'react-hot-toast';

const getInternalTab = (tab) => {
  const mapping = {
    'operation-dashboard': 'dashboard',
    'operation-users': 'users',
    'operation-content': 'content',
    'operation-classic-questions': 'classic'
  };
  return mapping[tab] || 'dashboard';
};

const getApiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  const message = error?.response?.data?.message;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (typeof message === 'string' && message.trim()) return message.trim();
  if (typeof error?.message === 'string' && error.message.trim() && !error.message.includes('status code')) {
    return error.message.trim();
  }
  return fallback;
};

const getUpgradeDurationDays = (level) => {
  if (level === 'L1') return 7;
  if (level === 'L2') return 30;
  return 90;
};

const getEstimatedUpgradeExpiryText = (level) => {
  const days = getUpgradeDurationDays(level);
  const expiry = new Date();
  expiry.setDate(expiry.getDate() + days);
  return expiry.toLocaleDateString('zh-CN');
};

const OperationDashboard = ({ initialTab = 'operation-dashboard' }) => {
  const [activeTab, setActiveTab] = useState(getInternalTab(initialTab));
  const [newsList, setNewsList] = useState([]);
  const [newsSearch, setNewsSearch] = useState('');
  const [newsPage, setNewsPage] = useState(1);
  const [newsPageSize] = useState(10);
  const [newsTotal, setNewsTotal] = useState(0);
  const [newsTotalPages, setNewsTotalPages] = useState(1);
  const [classicQuestions, setClassicQuestions] = useState([]);
  const [classicLoading, setClassicLoading] = useState(false);
  const [classicError, setClassicError] = useState('');
  const [classicPage, setClassicPage] = useState(1);
  const [classicPageSize] = useState(10);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    archived: 0
  });
  const classicFormInitialState = {
    question: '',
    category: '',
    difficulty: 'medium',
    language: 'zh',
    guidance: '',
    sample_answer: '',
    tagsText: ''
  };
  const [classicForm, setClassicForm] = useState(classicFormInitialState);
  const [showClassicModal, setShowClassicModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  // 用户管理状态
  const [searchFilters, setSearchFilters] = useState({
    nickname: '',
    email: '',
    phone: '',
    memberType: '',
    sortBy: '', // 'points' | 'points_change'
    sortOrder: '' // 'asc' | 'desc'
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [actionType, setActionType] = useState(''); // blacklist, freeze
  const [actionReason, setActionReason] = useState('');
  const [freezeDays, setFreezeDays] = useState(7);

  // 积分调整状态
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [pointsChange, setPointsChange] = useState(0);
  const [pointsReason, setPointsReason] = useState('');
  const [pointsTemplate, setPointsTemplate] = useState(''); // 预设模板
  const [pointsOperationPassword, setPointsOperationPassword] = useState('');
  const [pointsModalStep, setPointsModalStep] = useState('form'); // 'form' | 'confirm'
  const [pointsSubmitting, setPointsSubmitting] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalStep, setUpgradeModalStep] = useState('form'); // 'form' | 'confirm'
  const [upgradeSubmitting, setUpgradeSubmitting] = useState(false);
  const [upgradeTargetLevel, setUpgradeTargetLevel] = useState('L1');
  const [upgradeReason, setUpgradeReason] = useState('');
  const [upgradeOperationPassword, setUpgradeOperationPassword] = useState('');

  // 用户详情状态
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // 搜索和分页状态
  const [userPage, setUserPage] = useState(1);
  const [userPageSize] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isSearching, setIsSearching] = useState(false);

  // 运营统计数据
  const [dashboardStats, setDashboardStats] = useState({
    total_users: 0,
    today_new_users: 0,
    active_users: 0,
    paid_users: 0,
    daily_active_users: 0,
    monthly_active_users: 0,
    blacklist_users: 0,
    frozen_users: 0
  });

  // 图表显示类型状态
  const [chartType, setChartType] = useState('new'); // 'new' 或 'cumulative'
  const [revenueChartType, setRevenueChartType] = useState('revenue'); // 'revenue' 或 'users'
  const [moduleChartType, setModuleChartType] = useState('mock_interview'); // 功能使用图表类型，默认显示模拟面试
  const [retentionType, setRetentionType] = useState('next_day'); // 留存率类型
  const [activityType, setActivityType] = useState('dau'); // 活跃度类型
  const [conversionType, setConversionType] = useState('register_to_active'); // 转化率类型
  const [durationType, setDurationType] = useState('mock_interview'); // 使用时长类型

  // 数据分析统计
  const [analyticsStats, setAnalyticsStats] = useState({
    users: {
      total: 0,
      today: 0,
      week: 0,
      month: 0,
      online: 0,
      shared_registrations: 0
    },
    activity: {
      active_users: 0,
      interview_count: 0,
      avg_session_time: 0
    },
    revenue: {
      total: 0,
      today: 0,
      week: 0,
      month: 0,
      paid_users: 0
    },
    module_usage: {
      job_assistant: 0,
      resume_diagnosis: 0,
      interview_prep: 0,
      mock_interview: 0,
      meeting_assistant: 0
    }
  });

  const [dailyTimeRange, setDailyTimeRange] = useState('week'); // 每日用户趋势独立时间范围
  const [revenueTimeRange, setRevenueTimeRange] = useState('week'); // 付费/收入趋势独立时间范围
  const [moduleTimeRange, setModuleTimeRange] = useState('week'); // 功能使用趋势独立时间范围
  const [durationTimeRange, setDurationTimeRange] = useState('week'); // 功能平均时长趋势独立时间范围
  const [apiCostTimeRange, setApiCostTimeRange] = useState('week'); // API费用趋势时间范围
  const [resumeCostTimeRange, setResumeCostTimeRange] = useState('week'); // 简历生成费用趋势时间范围
  const [apiPerformanceWindow, setApiPerformanceWindow] = useState(60); // API性能监控时间窗口（分钟）
  const [refreshingPanel, setRefreshingPanel] = useState(null);

  // 趋势图使用的每日统计数据（后端真实数据）
  const [dailyStats, setDailyStats] = useState(null);
  const [moduleUsageBackend, setModuleUsageBackend] = useState(null);
  const [resumeGenerationOverview, setResumeGenerationOverview] = useState({
    resume_generation_total: 0,
    resume_assist_write_total: 0,
    resume_pdf_export_total: 0,
    resume_voice_input_total: 0,
    resume_export_points_total: 0
  });
  const [featureDurationBackend, setFeatureDurationBackend] = useState(null);
  const [apiCostBackend, setApiCostBackend] = useState(null);
  const [resumeCostBackend, setResumeCostBackend] = useState(null);
  const [apiPerformance, setApiPerformance] = useState({
    window_minutes: 60,
    total_calls: 0,
    calls_with_duration: 0,
    avg_duration_seconds: 0,
    max_duration_seconds: 0,
    total_cost: 0,
    by_type: {}
  });

  // 表单状态
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    summary: '',
    category: 'industry_trend',
    status: 'draft',
    priority: 0,
    is_featured: false,
    tags: []
  });

  useEffect(() => {
    loadDashboardStats();
    if (activeTab === 'dashboard') {
      // 初始化各图表数据由下方专用 effect 负责，这里不再触发旧的 loadDailyStats
    }
    if (activeTab === 'content') {
      loadNewsList();
      loadStats();
    }
    if (activeTab === 'classic') {
      loadClassicQuestions();
    }
    // 用户管理页面加载时显示示例数据
    if (activeTab === 'users') {
      loadSampleUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // 监听分页变化
  useEffect(() => {
    const hasSearch = searchFilters.nickname || searchFilters.email || searchFilters.phone;
    if (activeTab === 'users' && !hasSearch) {
      loadSampleUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPage]);

  // 监听 initialTab 变化，更新 activeTab
  useEffect(() => {
    setActiveTab(getInternalTab(initialTab));
  }, [initialTab]);

  const handleSort = (field) => {
    let newOrder = 'desc';
    if (searchFilters.sortBy === field) {
      if (searchFilters.sortOrder === 'desc') newOrder = 'asc';
      else if (searchFilters.sortOrder === 'asc') {
        newOrder = '';
        field = '';
      }
    }
    
    // 立即更新状态，并通过回调或者useEffect触发查询
    setSearchFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: newOrder
    }));
    setUserPage(1);
  };

  // 监听排序变化
  useEffect(() => {
    if (activeTab === 'users') {
      const hasSearch = searchFilters.nickname || searchFilters.email || searchFilters.phone || searchFilters.memberType;
      if (hasSearch || searchFilters.sortBy) {
        handleUserSearch();
      } else {
        loadSampleUsers();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilters.sortBy, searchFilters.sortOrder]);

  // 加载用户数据（从后端获取）
  const loadSampleUsers = async (forceClear = false) => {
    try {
      setLoading(true);
      setError('');

      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');

      if (!adminUser || !token) {
        setError('管理员未登录，请重新登录后台');
        setUsers([]);
        setTotalUsers(0);
        setIsSearching(false);
        return;
      }

      // 构建基础URL
      let url = `/api/v1/admin/users?page=${userPage}&page_size=${userPageSize}`;

      // 只有在非强制清空时才添加搜索参数
      if (!forceClear) {
        if (searchFilters.nickname) {
          url += `&nickname=${encodeURIComponent(searchFilters.nickname)}`;
        }
        if (searchFilters.email) {
          url += `&email=${encodeURIComponent(searchFilters.email)}`;
        }
        if (searchFilters.phone) {
          url += `&phone=${encodeURIComponent(searchFilters.phone)}`;
        }
        if (searchFilters.memberType) {
          url += `&member_type=${searchFilters.memberType}`;
        }
        if (searchFilters.sortBy) {
          url += `&sort_by=${searchFilters.sortBy}`;
        }
        if (searchFilters.sortOrder) {
          url += `&sort_order=${searchFilters.sortOrder}`;
        }
      }
      // 当forceClear为true时，直接使用基础URL，不添加任何搜索参数

      const response = await adminApi.get(url);
      const data = response.data;

      if (data.success) {
        const apiUsers = (data.data && data.data.users) || [];
        const pagination = data.data && data.data.pagination;
        setUsers(apiUsers);
        setTotalUsers((pagination && pagination.total) || apiUsers.length || 0);
      } else {
        setError(data.message || '获取用户列表失败');
        setUsers([]);
        setTotalUsers(0);
      }
    } catch (err) {
      setError('加载用户列表失败，请稍后重试');
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const loadModuleUsage = async (daysParam) => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');
      if (!adminUser || !token) return;
      const days = typeof daysParam === 'number' ? daysParam : (moduleTimeRange === 'week' ? 7 : 30);
      const res = await adminApi.get(`/api/v1/admin/analytics/module-usage?days=${days}`);
      const data = res.data;
      if (data.success && data.data && Array.isArray(data.data.module_usage)) {
        setModuleUsageBackend(data.data.module_usage);
        setResumeGenerationOverview(data.data.resume_generation_overview || {
          resume_generation_total: 0,
          resume_assist_write_total: 0,
          resume_pdf_export_total: 0,
          resume_voice_input_total: 0,
          resume_export_points_total: 0
        });
        const totals = data.data.module_usage.reduce((acc, item) => {
          acc.job_assistant += (item.job_search || 0);
          acc.resume_diagnosis += (item.resume_diagnosis || 0);
          acc.interview_prep += (item.interview_questions || 0);
          acc.mock_interview += (item.mock_interview || 0);
          acc.meeting_assistant += (item.meeting_assistant || 0);
          return acc;
        }, { job_assistant: 0, resume_diagnosis: 0, interview_prep: 0, mock_interview: 0, meeting_assistant: 0 });
        setAnalyticsStats(prev => ({
          ...prev,
          module_usage: totals
        }));
      }
    } catch (err) {
    }
  };

  const loadFeatureDuration = async (daysParam) => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');
      if (!adminUser || !token) return;
      const days = typeof daysParam === 'number' ? daysParam : (durationTimeRange === 'week' ? 7 : 30);
      const res = await adminApi.get(`/api/v1/admin/analytics/feature-duration?days=${days}`);
      const data = res.data;
      if (data.success && data.data && Array.isArray(data.data.feature_duration)) {
        setFeatureDurationBackend(data.data.feature_duration);
      }
    } catch (err) {
    }
  };

  const loadApiCostTrend = async (daysParam) => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');
      if (!adminUser || !token) return;
      const days = typeof daysParam === 'number' ? daysParam : (apiCostTimeRange === 'week' ? 7 : 30);
      const res = await adminApi.get(`/api/v1/admin/analytics/api-call-trend?days=${days}`);
      const data = res.data;
      if (data.success && data.data && Array.isArray(data.data.daily_api_costs)) {
        setApiCostBackend(data.data.daily_api_costs);
      }
    } catch (err) {
    }
  };

  const loadResumeGenerationCostTrend = async (daysParam) => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');
      if (!adminUser || !token) return;
      const days = typeof daysParam === 'number' ? daysParam : (resumeCostTimeRange === 'week' ? 7 : 30);
      const res = await adminApi.get(`/api/v1/admin/analytics/resume-generation-cost-trend?days=${days}`);
      const data = res.data;
      if (data.success && data.data && Array.isArray(data.data.daily_resume_costs)) {
        setResumeCostBackend(data.data.daily_resume_costs);
      }
    } catch (err) {
    }
  };

  const loadApiPerformance = async (windowMinutesParam) => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');
      if (!adminUser || !token) return;
      const windowMinutes = typeof windowMinutesParam === 'number' ? windowMinutesParam : apiPerformanceWindow;
      const res = await adminApi.get(`/api/v1/admin/analytics/api-performance?window_minutes=${windowMinutes}`);
      const data = res.data;
      if (data.success && data.data) {
        setApiPerformance({
          window_minutes: data.data.window_minutes || windowMinutes,
          total_calls: data.data.total_calls || 0,
          calls_with_duration: data.data.calls_with_duration || 0,
          avg_duration_seconds: data.data.avg_duration_seconds || 0,
          max_duration_seconds: data.data.max_duration_seconds || 0,
          total_cost: data.data.total_cost || 0,
          by_type: data.data.by_type || {}
        });
      }
    } catch (err) {
    }
  };

  // 独立加载每日用户趋势（只更新 dailyStats.daily_users）
  const loadDailyUsersStats = async (daysParam) => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');
      if (!adminUser || !token) return;
      const days = typeof daysParam === 'number' ? daysParam : (dailyTimeRange === 'week' ? 7 : 30);
      const res = await adminApi.get(`/api/v1/admin/analytics/daily-stats?days=${days}`);
      const data = res.data;
      if (data.success && data.data) {
        const dailyUsers = Array.isArray(data.data.daily_users) ? data.data.daily_users : [];
        setDailyStats(prev => ({
          ...(prev || {}),
          daily_users: dailyUsers
        }));
      }
    } catch (err) {
    }
  };

  // 独立加载每日收入与付费用户趋势（只更新 dailyStats.daily_revenue）
  const loadDailyRevenueStats = async (daysParam) => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');

      if (!adminUser || !token) {
        return;
      }

      const days = typeof daysParam === 'number' ? daysParam : (revenueTimeRange === 'week' ? 7 : 30);
      const response = await adminApi.get(`/api/v1/admin/analytics/daily-stats?days=${days}`);
      const data = response.data;

      if (data.success && data.data) {
        const dailyRevenue = Array.isArray(data.data.daily_revenue) ? data.data.daily_revenue : [];
        setDailyStats(prev => ({
          ...(prev || {}),
          daily_revenue: dailyRevenue
        }));
      }
    } catch (err) {
    }
  };

  const handleRefreshRevenue = async () => {
    try {
      setRefreshingPanel('revenue');
      await loadDailyRevenueStats(revenueTimeRange === 'week' ? 7 : 30);
    } finally {
      setRefreshingPanel(null);
    }
  };

  const handleRefreshDailyUsers = async () => {
    try {
      setRefreshingPanel('daily');
      await loadDailyUsersStats(dailyTimeRange === 'week' ? 7 : 30);
    } finally {
      setRefreshingPanel(null);
    }
  };

  const handleRefreshDuration = async () => {
    try {
      setRefreshingPanel('duration');
      await loadFeatureDuration(durationTimeRange === 'week' ? 7 : 30);
    } finally {
      setRefreshingPanel(null);
    }
  };

  const handleRefreshModuleUsage = async () => {
    try {
      setRefreshingPanel('module');
      await loadModuleUsage(moduleTimeRange === 'week' ? 7 : 30);
    } finally {
      setRefreshingPanel(null);
    }
  };

  const handleRefreshApiPerformance = async () => {
    try {
      setRefreshingPanel('apiPerformance');
      await loadApiPerformance(apiPerformanceWindow);
    } finally {
      setRefreshingPanel(null);
    }
  };

  // 进入看板时初始化各图的当前时间范围数据
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDailyUsersStats(dailyTimeRange === 'week' ? 7 : 30);
      loadDailyRevenueStats(revenueTimeRange === 'week' ? 7 : 30);
      loadModuleUsage(moduleTimeRange === 'week' ? 7 : 30);
      loadFeatureDuration(durationTimeRange === 'week' ? 7 : 30);
      loadApiCostTrend(apiCostTimeRange === 'week' ? 7 : 30);
      loadResumeGenerationCostTrend(resumeCostTimeRange === 'week' ? 7 : 30);
      loadApiPerformance(apiPerformanceWindow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // 监听API性能时间窗口变化
  useEffect(() => {
    if (activeTab === 'dashboard') {
      (async () => {
        setRefreshingPanel('apiPerformance');
        await loadApiPerformance(apiPerformanceWindow);
        setRefreshingPanel(null);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiPerformanceWindow, activeTab]);

  // 独立监听付费/收入趋势的时间范围变更，仅刷新该图数据
  useEffect(() => {
    if (activeTab === 'dashboard') {
      (async () => {
        setRefreshingPanel('revenue');
        await loadDailyRevenueStats(revenueTimeRange === 'week' ? 7 : 30);
        setRefreshingPanel(null);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revenueTimeRange, activeTab]);

  // 独立监听每日用户趋势的时间范围变更
  useEffect(() => {
    if (activeTab === 'dashboard') {
      (async () => {
        setRefreshingPanel('daily');
        await loadDailyUsersStats(dailyTimeRange === 'week' ? 7 : 30);
        setRefreshingPanel(null);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyTimeRange, activeTab]);

  // 独立监听功能使用趋势的时间范围变更
  useEffect(() => {
    if (activeTab === 'dashboard') {
      (async () => {
        setRefreshingPanel('module');
        await loadModuleUsage(moduleTimeRange === 'week' ? 7 : 30);
        setRefreshingPanel(null);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleTimeRange, activeTab]);

  // 独立监听功能平均时长趋势的时间范围变更
  useEffect(() => {
    if (activeTab === 'dashboard') {
      (async () => {
        setRefreshingPanel('duration');
        await loadFeatureDuration(durationTimeRange === 'week' ? 7 : 30);
        setRefreshingPanel(null);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationTimeRange, activeTab]);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      (async () => {
        setRefreshingPanel('api_cost');
        await loadApiCostTrend(apiCostTimeRange === 'week' ? 7 : 30);
        setRefreshingPanel(null);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCostTimeRange, activeTab]);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      (async () => {
        setRefreshingPanel('resume_cost');
        await loadResumeGenerationCostTrend(resumeCostTimeRange === 'week' ? 7 : 30);
        setRefreshingPanel(null);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeCostTimeRange, activeTab]);

  // 使用 useMemo 缓存图表数据，避免每次渲染都重新生成
  // 生成完整数据，图表内部按各自时间范围裁剪
  const chartData = useMemo(() => {
    // 生成所有30天的日期
    const generateDatesForCharts = () => {
      const startDate = new Date('2024-12-23');
      return Array.from({ length: 30 }, (_, i) => {
        const date = new Date(startDate);
        date.setDate(date.getDate() + i);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${month}-${day}`;
      });
    };

    const chartDates = generateDatesForCharts();

    // 生成完整的30天数据
    const fullDailyUserData = chartDates.map((date, i) => ({
      date,
      users: Math.round(20 + Math.sin(i * 0.25) * 20),
      cumulative: 980 + i * 30 + Math.sin(i * 0.1) * 50
    }));

    const fullRevenueData = chartDates.map((date, i) => ({
      date,
      revenue: Math.round(800 + Math.sin(i * 0.2) * 800),
      users: Math.round(5 + Math.sin(i * 0.15) * 8)
    }));

    // 用户留存率趋势数据
    const fullRetentionRateData = chartDates.map((date, i) => ({
      date,
      next_day: Math.round(45 + Math.sin(i * 0.3) * 5),
      seven_day: Math.round(32 + Math.sin(i * 0.2) * 6),
      thirty_day: Math.round(18 + Math.sin(i * 0.15) * 4)
    }));

    // 用户活跃度趋势数据
    const fullActivityData = chartDates.map((date, i) => ({
      date,
      dau: Math.round(350 + Math.sin(i * 0.3) * 100),
      mau: Math.round(3000 + Math.sin(i * 0.1) * 500),
      avg_session: Math.round(25 + Math.sin(i * 0.2) * 8)
    }));

    // 转化率趋势数据
    const fullConversionData = chartDates.map((date, i) => ({
      date,
      register_to_active: Math.round(65 + Math.sin(i * 0.2) * 10),
      visit_to_use: Math.round(40 + Math.sin(i * 0.25) * 12),
      free_to_paid: Math.round((7 + Math.sin(i * 0.15) * 3) * 10) / 10
    }));

    // 核心功能使用时长数据
    const fullFeatureDurationData = chartDates.map((date, i) => ({
      date,
      mock_interview: Math.round((16 + Math.sin(i * 0.2) * 6) * 10) / 10,
      meeting_assistant: Math.round((11 + Math.sin(i * 0.3) * 4) * 10) / 10
    }));

    // 功能使用趋势数据
    const fullModuleUsageData = chartDates.map((date, i) => ({
      date,
      job_search: Math.round(280 + Math.sin(i * 0.15) * 140),
      resume_diagnosis: Math.round(230 + Math.sin(i * 0.18) * 110),
      interview_questions: Math.round(170 + Math.sin(i * 0.2) * 90),
      mock_interview: Math.round(100 + Math.sin(i * 0.25) * 80),
      meeting_assistant: Math.round(70 + Math.sin(i * 0.3) * 50),
      resume_generation: Math.round(90 + Math.sin(i * 0.2) * 45),
      resume_assist_write: Math.round(75 + Math.sin(i * 0.24) * 35),
      resume_pdf_export: Math.round(30 + Math.sin(i * 0.18) * 16),
      resume_voice_input: Math.round(22 + Math.sin(i * 0.16) * 12),
      resume_export_points: Math.round(180 + Math.sin(i * 0.16) * 90)
    }));

    const backendModuleData = moduleUsageBackend && moduleUsageBackend.length
      ? moduleUsageBackend.map(item => ({
        date: (item.date || '').slice(5),
        job_search: item.job_search || 0,
        resume_diagnosis: item.resume_diagnosis || 0,
        interview_questions: item.interview_questions || 0,
        mock_interview: item.mock_interview || 0,
        meeting_assistant: item.meeting_assistant || 0,
        resume_generation: item.resume_generation || 0,
        resume_assist_write: item.resume_assist_write || 0,
        resume_pdf_export: item.resume_pdf_export || 0,
        resume_voice_input: item.resume_voice_input || 0,
        resume_export_points: item.resume_export_points || 0
      }))
      : null;

    const backendDurationData = featureDurationBackend && featureDurationBackend.length
      ? featureDurationBackend.map(item => ({
        date: (item.date || '').slice(5),
        mock_interview: typeof item.mock_interview === 'number' ? item.mock_interview : 0,
        meeting_assistant: typeof item.meeting_assistant === 'number' ? item.meeting_assistant : 0
      }))
      : null;

    return {
      dailyUserData: fullDailyUserData,
      revenueData: fullRevenueData,
      retentionRateData: fullRetentionRateData,
      activityData: fullActivityData,
      conversionData: fullConversionData,
      featureDurationData: backendDurationData || fullFeatureDurationData,
      moduleUsageData: backendModuleData || fullModuleUsageData,
      apiCostData: (apiCostBackend && apiCostBackend.length)
        ? apiCostBackend.map(item => ({
          date: (item.date || '').slice(5),
          asr: item.asr || 0,
          tts: item.tts || 0,
          llm: item.llm || 0
        }))
        : null,
      resumeCostData: (resumeCostBackend && resumeCostBackend.length)
        ? resumeCostBackend.map(item => ({
          date: (item.date || '').slice(5),
          resume_generation: item.resume_generation || 0,
          resume_assist_write: item.resume_assist_write || 0,
          resume_voice_input: item.resume_voice_input || 0,
          total: item.total || 0
        }))
        : null
    };
  }, [moduleUsageBackend, featureDurationBackend, apiCostBackend, resumeCostBackend]);

  const {
    dailyUserData,
    revenueData,
    retentionRateData,
    activityData,
    conversionData,
    featureDurationData,
    moduleUsageData,
    apiCostData,
    resumeCostData
  } = chartData;

  // 加载运营看板统计数据（真实后端）
  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      setError('');

      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');

      if (!adminUser || !token) {
        setError('管理员未登录，请重新登录后台');
        return;
      }

      const [dashboardRes, revenueRes] = await Promise.all([
        adminApi.get('/api/v1/admin/dashboard/stats'),
        adminApi.get('/api/v1/admin/revenue/stats')
      ]);

      const dashboardData = dashboardRes.data;
      const revenueData = revenueRes.data;

      if (dashboardData.success) {
        const baseStats = dashboardData.data || {};

        setDashboardStats(prev => ({
          ...prev,
          total_users: baseStats.total_users || 0,
          today_new_users: baseStats.today_new_users || 0,
          active_users: baseStats.online_users || 0,
          paid_users: baseStats.paid_users || 0,
          daily_active_users: baseStats.daily_active_users || 0,
          monthly_active_users: baseStats.monthly_active_users || 0,
          // 黑名单 / 冻结用户目前暂无后端字段，保持为 0
          blacklist_users: prev.blacklist_users || 0,
          frozen_users: prev.frozen_users || 0
        }));

        setAnalyticsStats(prev => ({
          ...prev,
          users: {
            ...prev.users,
            total: baseStats.total_users || 0,
            today: baseStats.today_new_users || 0,
            online: baseStats.online_users || 0,
            // week / month / shared_registrations 暂无汇总统计，先保持为 0
            week: prev.users.week || 0,
            month: prev.users.month || 0,
            shared_registrations: prev.users.shared_registrations || 0
          }
        }));
      } else {
        setError(dashboardData.message || '获取仪表板统计数据失败');
      }

      if (revenueData.success) {
        const r = revenueData.data || {};

        setAnalyticsStats(prev => ({
          ...prev,
          revenue: {
            ...prev.revenue,
            total: r.total_revenue || 0,
            today: r.today_revenue || 0,
            week: r.week_revenue || 0,
            month: r.month_revenue || 0,
            paid_users: r.paid_users || prev.revenue.paid_users || 0
          }
        }));
      } else {
        setError(revenueData.message || '获取收入统计失败');
      }
    } catch (err) {
      setError('加载运营统计数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const loadNewsList = async (pageArg, searchArg) => {
    try {
      setLoading(true);
      const page = typeof pageArg === 'number' ? pageArg : newsPage;
      const search = typeof searchArg === 'string' ? searchArg : newsSearch;
      let url = `/api/v1/workplace-news/?admin_view=true&page=${page}&page_size=${newsPageSize}`;
      if (search && search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      const response = await adminApi.get(url);
      const data = response.data;

      if (data.success) {
        setNewsList(data.data);
        const pg = data.pagination || {};
        setNewsTotal(pg.total || data.data?.length || 0);
        setNewsTotalPages(pg.total_pages || 1);
        setNewsPage(pg.page || page);
        setError('');
      } else {
        setError(data.message || '获取动态列表失败');
        setNewsList([]);
        setNewsTotal(0);
        setNewsTotalPages(1);
      }
    } catch (err) {
      setError('网络错误，请稍后重试');
      setNewsList([]);
      setNewsTotal(0);
      setNewsTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const loadClassicQuestions = async () => {
    try {
      setClassicLoading(true);
      setClassicError('');
      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');
      if (!adminUser || !token) {
        setClassicQuestions([]);
        setClassicError('管理员未登录，请重新登录后台');
        return;
      }
      const response = await adminApi.get('/api/v1/classic-questions/?limit=500&admin_view=true');
      const data = response.data;
      if (Array.isArray(data)) {
        setClassicQuestions(data);
      } else {
        setClassicQuestions([]);
      }
    } catch (err) {
      setClassicQuestions([]);
      setClassicError('加载经典题库失败，请稍后重试');
    } finally {
      setClassicLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await adminApi.get('/api/v1/workplace-news/admin/stats');
      const data = response.data;
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
    }
  };

  const resetClassicForm = () => {
    setClassicForm(classicFormInitialState);
  };

  const openClassicCreateModal = () => {
    setEditingQuestion(null);
    resetClassicForm();
    setShowClassicModal(true);
  };

  const openClassicEditModal = (question) => {
    setEditingQuestion(question);
    setClassicForm({
      question: question.question || '',
      category: question.category || '',
      difficulty: question.difficulty || 'medium',
      language: question.language || 'zh',
      guidance: question.guidance || '',
      sample_answer: question.sample_answer || '',
      tagsText: Array.isArray(question.tags) ? question.tags.join(',') : ''
    });
    setShowClassicModal(true);
  };

  const buildClassicPayload = () => {
    const tags = classicForm.tagsText
      ? classicForm.tagsText.split(',').map(t => t.trim()).filter(Boolean)
      : [];
    return {
      question: classicForm.question,
      category: classicForm.category || null,
      difficulty: classicForm.difficulty || 'medium',
      language: classicForm.language || 'zh',
      guidance: classicForm.guidance || null,
      sample_answer: classicForm.sample_answer || null,
      tags: tags.length ? tags : null,
      extra: null,
      is_published: true,
      views: 0
    };
  };

  const handleCreateClassicQuestion = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');
      if (!adminUser || !token) {
        toast.error('管理员未登录，请重新登录后台');
        return;
      }
      const payload = buildClassicPayload();
      console.log('Creating classic question with payload:', payload);
      const response = await adminApi.post('/api/v1/classic-questions/', payload);
      const data = response.data;
      if (data && data.id) {
        setShowClassicModal(false);
        setEditingQuestion(null);
        resetClassicForm();
        setClassicPage(1); // 跳转到第一页查看新创建的题目
        await loadClassicQuestions();
        toast.success('经典题目创建成功');
      } else {
        toast.error('创建失败');
      }
    } catch (err) {
      console.error('创建经典题目失败:', err);
      console.error('错误详情:', err.response?.data);
      const errorMsg = err.response?.data?.detail || err.message || '网络错误，请稍后重试';
      toast.error(`创建失败: ${errorMsg}`);
    }
  };

  const handleUpdateClassicQuestion = async () => {
    if (!editingQuestion) return;
    try {
      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');
      if (!adminUser || !token) {
        toast.error('管理员未登录，请重新登录后台');
        return;
      }
      const payload = buildClassicPayload();
      console.log('Updating classic question with payload:', payload);
      const response = await adminApi.patch(`/api/v1/classic-questions/${editingQuestion.id}`, payload);
      const data = response.data;
      if (data && data.id) {
        setShowClassicModal(false);
        setEditingQuestion(null);
        resetClassicForm();
        await loadClassicQuestions();
        toast.success('经典题目更新成功');
      } else {
        toast.error('更新失败');
      }
    } catch (err) {
      console.error('更新经典题目失败:', err);
      console.error('错误详情:', err.response?.data);
      const errorMsg = err.response?.data?.detail || err.message || '网络错误，请稍后重试';
      toast.error(`更新失败: ${errorMsg}`);
    }
  };

  const handleDeleteClassicQuestion = async (id) => {
    if (!window.confirm('确定要删除这道题目吗？')) return;
    try {
      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');
      if (!adminUser || !token) {
        toast.error('管理员未登录，请重新登录后台');
        return;
      }
      const response = await adminApi.delete(`/api/v1/classic-questions/${id}`);
      const data = response.data;
      if (data && data.success) {
        await loadClassicQuestions();
        // 删除后检查当前页是否还有数据，如果没有则跳转到上一页
        const remainingItems = classicQuestions.length - 1;
        const totalPages = Math.ceil(remainingItems / classicPageSize);
        if (classicPage > totalPages && totalPages > 0) {
          setClassicPage(totalPages);
        }
        toast.success('经典题目删除成功');
      } else {
        toast.error(data.message || '删除失败');
      }
    } catch (err) {
      toast.error('网络错误，请稍后重试');
    }
  };

  const handleCreateNews = async () => {
    try {
      const response = await adminApi.post('/api/v1/workplace-news/', formData);
      const data = response.data;
      if (data.success) {
        setShowCreateModal(false);
        resetForm();
        loadNewsList(1, newsSearch);
        loadStats();
        toast.success('动态创建成功');
      } else {
        toast.error(data.message || '创建失败');
      }
    } catch (err) {
      toast.error('网络错误，请稍后重试');
    }
  };

  const handleUpdateNews = async () => {
    try {
      const response = await adminApi.put(`/api/v1/workplace-news/${editingNews.news_id}`, formData);
      const data = response.data;
      if (data.success) {
        setEditingNews(null);
        resetForm();
        loadNewsList(newsPage, newsSearch);
        loadStats();
        toast.success('动态更新成功');
      } else {
        toast.error(data.message || '更新失败');
      }
    } catch (err) {
      toast.error('网络错误，请稍后重试');
    }
  };

  const handleDeleteNews = async (newsId) => {
    if (!window.confirm('确定要删除这条动态吗？')) return;

    try {
      const response = await adminApi.delete(`/api/v1/workplace-news/${newsId}`);
      const data = response.data;
      if (data.success) {
        const newPage = newsPage > 1 && (newsTotal - 1) <= (newsPage - 1) * newsPageSize ? newsPage - 1 : newsPage;
        loadNewsList(newPage, newsSearch);
        loadStats();
        toast.success('动态删除成功');
      } else {
        toast.error(data.message || '删除失败');
      }
    } catch (err) {
      toast.error('网络错误，请稍后重试');
    }
  };

  const handlePublishNews = async (newsId) => {
    try {
      const response = await adminApi.post(`/api/v1/workplace-news/${newsId}/publish`);
      const data = response.data;
      if (data.success) {
        loadNewsList(newsPage, newsSearch);
        loadStats();
        toast.success('动态发布成功');
      } else {
        toast.error(data.message || '发布失败');
      }
    } catch (err) {
      alert('网络错误，请稍后重试');
    }
  };

  const handleArchiveNews = async (newsId) => {
    try {
      const response = await adminApi.post(`/api/v1/workplace-news/${newsId}/archive`);
      const data = response.data;
      if (data.success) {
        loadNewsList(newsPage, newsSearch);
        loadStats();
        toast.success('动态归档成功');
      } else {
        toast.error(data.message || '归档失败');
      }
    } catch (err) {
      toast.error('网络错误，请稍后重试');
    }
  };

  const handleNewsSearch = async () => {
    setNewsPage(1);
    await loadNewsList(1, newsSearch);
  };

  const handleClearNewsSearch = async () => {
    setNewsSearch('');
    setNewsPage(1);
    await loadNewsList(1, '');
  };

  const handleNewsPrevPage = async () => {
    if (newsPage > 1) {
      const newPage = newsPage - 1;
      setNewsPage(newPage);
      await loadNewsList(newPage, newsSearch);
    }
  };

  const handleNewsNextPage = async () => {
    if (newsPage < newsTotalPages) {
      const newPage = newsPage + 1;
      setNewsPage(newPage);
      await loadNewsList(newPage, newsSearch);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      summary: '',
      category: 'industry_trend',
      status: 'draft',
      priority: 0,
      is_featured: false,
      tags: []
    });
  };

  const openEditModal = (news) => {
    setEditingNews(news);
    setFormData({
      title: news.title,
      content: news.content || '',
      summary: news.summary || '',
      category: news.category,
      status: news.status,
      priority: news.priority,
      is_featured: news.is_featured,
      tags: news.tags || []
    });
    setShowCreateModal(true);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'draft': { text: '草稿', class: 'status-draft' },
      'published': { text: '已发布', class: 'status-published' },
      'archived': { text: '已归档', class: 'status-archived' }
    };
    const statusInfo = statusMap[status] || { text: status, class: 'status-default' };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const getCategoryText = (category) => {
    const categoryMap = {
      'industry_trend': '行业趋势',
      'job_market': '就业市场',
      'salary_info': '薪资信息',
      'company_news': '公司动态',
      'skill_trend': '技能趋势',
      'career_tips': '职业建议',
      'other': '其他'
    };
    return categoryMap[category] || category;
  };

  // 用户搜索功能
  const handleUserSearch = async () => {
    const hasSearch = searchFilters.nickname || searchFilters.email || searchFilters.phone || searchFilters.memberType;

    if (!hasSearch) {
      // 如果搜索为空，清空搜索并重置分页，回退到列表模式
      await loadSampleUsers();
      return;
    }

    try {
      setIsSearching(true);
      setLoading(true);

      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');

      if (!adminUser || !token) {
        setError('管理员未登录，请重新登录后台');
        setUsers([]);
        setTotalUsers(0);
        return;
      }

      let url = `/api/v1/admin/users?page=1&page_size=${userPageSize}`;
      if (searchFilters.nickname) {
        url += `&nickname=${encodeURIComponent(searchFilters.nickname)}`;
      }
      if (searchFilters.email) {
        url += `&email=${encodeURIComponent(searchFilters.email)}`;
      }
      if (searchFilters.phone) {
        url += `&phone=${encodeURIComponent(searchFilters.phone)}`;
      }
      if (searchFilters.memberType) {
        url += `&member_type=${searchFilters.memberType}`;
      }
      if (searchFilters.sortBy) {
        url += `&sort_by=${searchFilters.sortBy}`;
      }
      if (searchFilters.sortOrder) {
        url += `&sort_order=${searchFilters.sortOrder}`;
      }

      const response = await adminApi.get(url);
      const data = response.data;

      if (data.success) {
        const apiUsers = (data.data && data.data.users) || [];
        if (apiUsers.length === 0) {
          setError('未找到匹配的用户');
          setUsers([]);
          setTotalUsers(0);
        } else {
          setUsers(apiUsers);
          setTotalUsers(
            (data.data && data.data.pagination && data.data.pagination.total) ||
            apiUsers.length
          );
          setError('');
        }
      } else {
        setError(data.message || '搜索失败，请稍后重试');
        setUsers([]);
        setTotalUsers(0);
      }
    } catch (err) {
      setError('搜索失败，请稍后重试');
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  // 清除搜索
  const handleClearSearch = async () => {
    setSearchFilters({
      nickname: '',
      email: '',
      phone: '',
      memberType: ''
    });
    setUserPage(1);
    setIsSearching(false);
    await loadSampleUsers(true);
  };

  // 打开用户操作弹窗
  const openUserActionModal = (user, action) => {
    setSelectedUser(user);
    setActionType(action);
    setActionReason('');
    setFreezeDays(7);
    setShowUserModal(true);
  };

  // 执行用户操作
  const handleUserAction = async () => {
    if (!actionReason.trim()) {
      alert('请输入操作原因');
      return;
    }

    try {
      const userId = selectedUser?.user_id;
      if (!userId) {
        alert('无效的用户ID');
        return;
      }
      let url = '';
      let body = {};
      if (actionType === 'freeze') {
        url = `/api/v1/admin/users/${userId}/freeze`;
        body = { reason: actionReason, days: freezeDays };
      } else {
        url = `/api/v1/admin/users/${userId}/blacklist`;
        body = { reason: actionReason };
      }
      const res = await adminApi.post(url, body);
      const data = res.data;
      if (!data.success) {
        throw new Error(data.detail || data.message || '操作失败');
      }
      alert(`${actionType === 'blacklist' ? '拉黑' : '冻结'}成功！\n用户: ${selectedUser.nickname}\n原因: ${actionReason}${actionType === 'freeze' ? `\n冻结天数: ${freezeDays}天` : ''}`);
      setShowUserModal(false);
      handleUserSearch(); // 刷新搜索结果
    } catch (err) {
      alert(err.message || '操作失败，请稍后重试');
    }
  };

  // 解冻用户
  const handleUnfreeze = async (user) => {
    if (!window.confirm(`确定要解冻用户 ${user.nickname} 吗？`)) return;

    try {
      const res = await adminApi.post(`/api/v1/admin/users/${user.user_id}/unfreeze`);
      const data = res.data;
      if (!data.success) {
        throw new Error(data.detail || data.message || '解冻失败');
      }
      alert(`用户 ${user.nickname} 已解冻`);
      handleUserSearch();
    } catch (err) {
      alert(err.message || '解冻失败，请稍后重试');
    }
  };

  // 移除黑名单
  const handleRemoveBlacklist = async (user) => {
    if (!window.confirm(`确定要将用户 ${user.nickname} 移出黑名单吗？`)) return;

    try {
      const res = await adminApi.post(`/api/v1/admin/users/${user.user_id}/remove-blacklist`);
      const data = res.data;
      if (!data.success) {
        throw new Error(data.detail || data.message || '移除失败');
      }
      alert(`用户 ${user.nickname} 已移出黑名单`);
      handleUserSearch();
    } catch (err) {
      alert(err.message || '移除失败，请稍后重试');
    }
  };

  const openUpgradeModal = (user) => {
    setSelectedUser(user);
    setUpgradeModalStep('form');
    setUpgradeSubmitting(false);
    setUpgradeTargetLevel('L1');
    setUpgradeReason('');
    setUpgradeOperationPassword('');
    setShowUpgradeModal(true);
  };

  const closeUpgradeModal = () => {
    setShowUpgradeModal(false);
    setUpgradeModalStep('form');
    setUpgradeSubmitting(false);
  };

  const validateUpgradeForm = () => {
    if (!selectedUser?.user_id) {
      toast.error('无效的用户ID');
      return false;
    }
    if (selectedUser.level !== 'L0') {
      toast.error('仅支持将体验版 L0 升级为会员等级');
      return false;
    }
    if (!upgradeReason.trim()) {
      toast.error('请填写升级原因');
      return false;
    }
    if (!upgradeOperationPassword.trim()) {
      toast.error('请输入操作口令');
      return false;
    }
    return true;
  };

  const handleUpgradeGoToConfirm = () => {
    if (!validateUpgradeForm()) return;
    setUpgradeModalStep('confirm');
  };

  const handleUpgradeExecute = async () => {
    if (!validateUpgradeForm()) return;

    setUpgradeSubmitting(true);
    try {
      const response = await adminApi.post(`/api/v1/admin/users/${selectedUser.user_id}/upgrade-member`, {
        target_level: upgradeTargetLevel,
        reason: upgradeReason.trim(),
        operator_password: upgradeOperationPassword.trim()
      });
      const data = response.data;
      if (!data.success) {
        throw new Error(data.detail || data.message || '升级失败');
      }
      const endDateText = data?.data?.end_date
        ? new Date(data.data.end_date).toLocaleDateString('zh-CN')
        : getEstimatedUpgradeExpiryText(upgradeTargetLevel);
      toast.success(`升级成功：${selectedUser.nickname}（${selectedUser.level} → ${upgradeTargetLevel}，到期：${endDateText}）`);
      closeUpgradeModal();
      await handleUserSearch();
    } catch (err) {
      toast.error(getApiErrorMessage(err, '升级失败，请稍后重试'));
    } finally {
      setUpgradeSubmitting(false);
    }
  };

  const formatLatestPointsChange = (change) => {
    if (typeof change !== 'number') return '-';
    if (change === 0) return '0';
    return `${change > 0 ? '+' : ''}${change}`;
  };

  const formatCompactDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    const pad = (num) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const handleUserActionSelect = async (user, action) => {
    if (!action) return;
    if (action === 'points') {
      openPointsModal(user);
      return;
    }
    if (action === 'freeze' || action === 'blacklist') {
      openUserActionModal(user, action);
      return;
    }
    if (action === 'upgrade_member') {
      openUpgradeModal(user);
      return;
    }
    if (action === 'unfreeze') {
      await handleUnfreeze(user);
      return;
    }
    if (action === 'remove_blacklist') {
      await handleRemoveBlacklist(user);
    }
  };

  const closePointsModal = () => {
    setShowPointsModal(false);
    setPointsModalStep('form');
    setPointsSubmitting(false);
  };

  // 打开积分调整弹窗
  const openPointsModal = (user) => {
    setSelectedUser(user);
    setPointsModalStep('form');
    setPointsSubmitting(false);
    setShowPointsModal(true);
    setPointsChange(0);
    setPointsReason('');
    setPointsTemplate('');
    setPointsOperationPassword('');
  };

  // 打开用户详情弹窗
  const openUserDetailsModal = async (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
    setLoadingDetails(true);

    try {
      const adminUser = JSON.parse(localStorage.getItem('admin_user'));
      const token = localStorage.getItem('admin_token');

      if (!adminUser || !token) {
        setError('管理员未登录，请重新登录后台');
        setUserDetails(null);
        setLoadingDetails(false);
        return;
      }

      const response = await adminApi.get(`/api/v1/admin/users/${user.user_id}/details`);
      const data = response.data;

      if (data.success) {
        setUserDetails(data.data);
      } else {
        setError(data.message || '获取用户详情失败');
        setUserDetails(null);
      }
    } catch (err) {
      setError('获取用户详情失败，请稍后重试');
      setUserDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  // 应用预设模板
  const applyPointsTemplate = (template) => {
    setPointsTemplate(template);
    const templates = {
      'compensation': { change: 100, reason: '系统补偿' },
      'reward': { change: 100, reason: '活动奖励' },
      'penalty': { change: -50, reason: '违规处罚' },
      'refund': { change: 50, reason: '退款补偿' }
    };

    if (templates[template]) {
      setPointsChange(templates[template].change);
      setPointsReason(templates[template].reason);
    }
  };

  const validatePointsForm = () => {
    if (!selectedUser?.user_id) {
      toast.error('无效的用户');
      return false;
    }
    if (pointsChange === 0) {
      toast.error('请输入有效的积分变化值');
      return false;
    }
    if (!pointsReason.trim()) {
      toast.error('请输入调整原因');
      return false;
    }
    if (pointsChange > 100) {
      toast.error('单次积分调整最高只能增加100分');
      return false;
    }
    if (!pointsOperationPassword.trim()) {
      toast.error('请输入操作口令');
      return false;
    }
    return true;
  };

  const handlePointsGoToConfirm = () => {
    if (!validatePointsForm()) return;
    setPointsModalStep('confirm');
  };

  const handlePointsExecute = async () => {
    if (!validatePointsForm()) return;

    setPointsSubmitting(true);
    try {
      const response = await adminApi.put(`/api/v1/admin/users/${selectedUser.user_id}/points`, {
        points_change: pointsChange,
        reason: pointsReason.trim(),
        operator_password: pointsOperationPassword.trim()
      });
      const data = response.data;

      if (data.success) {
        toast.success(
          `积分已调整：${selectedUser.nickname} ${pointsChange > 0 ? '+' : ''}${pointsChange} → 新积分 ${data.data.new_points}`
        );
        closePointsModal();
        setPointsChange(0);
        setPointsReason('');
        setPointsTemplate('');
        setPointsOperationPassword('');
        setUsers(users.map(u =>
          u.user_id === selectedUser.user_id
            ? { ...u, point: data.data.new_points }
            : u
        ));
      } else {
        toast.error(data.detail || data.message || '调整失败');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, '调整失败，请稍后重试'));
    } finally {
      setPointsSubmitting(false);
    }
  };

  const StatCard = ({ title, value }) => (
    <div className="stat-card">
      <div className="stat-content">
        <div className="stat-title">{title}</div>
        <div className="stat-value">{value}</div>
      </div>
    </div>
  );

  const renderClassicManagement = () => {
    // 计算分页数据
    const classicTotalPages = Math.ceil(classicQuestions.length / classicPageSize);
    const classicStartIndex = (classicPage - 1) * classicPageSize;
    const classicEndIndex = classicStartIndex + classicPageSize;
    const classicCurrentPageData = classicQuestions.slice(classicStartIndex, classicEndIndex);

    return (
      <div className="content-management">
        <div className="content-header">
          <h3>经典题库管理</h3>
        </div>

        <div className="news-section">
          <div className="table-section-header">
            <h4>经典题库管理</h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div className="table-section-info">
                共 {classicQuestions.length} 道题目
              </div>
              <button
                className="btn btn-primary"
                onClick={openClassicCreateModal}
              >
                <span>创建经典题目</span>
              </button>
            </div>
          </div>
          {classicLoading ? (
            <div className="loading">加载中...</div>
          ) : classicError ? (
            <div style={{ color: '#e53e3e', padding: '12px 0' }}>{classicError}</div>
          ) : classicQuestions.length > 0 ? (
            <>
              <div className="news-table">
                <div className="news-table-header">
                  <div className="news-col-id">序号</div>
                  <div className="news-col-title">问题</div>
                  <div className="news-col-category">分类</div>
                  <div className="news-col-status">难度</div>
                  <div className="news-col-featured">语言</div>
                  <div className="news-col-views">浏览量</div>
                  <div className="news-col-time">创建时间</div>
                  <div className="news-col-actions">操作</div>
                </div>
                {classicCurrentPageData.map((q, index) => (
                  <div key={q.id} className="news-table-row">
                    <div className="news-col-id">{classicStartIndex + index + 1}</div>
                    <div className="news-col-title" title={q.question}>{q.question}</div>
                    <div className="news-col-category">{q.category || '-'}</div>
                    <div className="news-col-status">{q.difficulty || '-'}</div>
                    <div className="news-col-featured">{q.language || '-'}</div>
                    <div className="news-col-views">{typeof q.views === 'number' ? q.views : 0}</div>
                    <div className="news-col-time">
                      {q.created_at ? new Date(q.created_at).toLocaleString() : '-'}
                    </div>
                    <div className="news-col-actions">
                      <button
                        className="btn-table btn-secondary"
                        onClick={() => openClassicEditModal(q)}
                      >
                        编辑
                      </button>
                      <button
                        className="btn-table btn-danger"
                        onClick={() => handleDeleteClassicQuestion(q.id)}
                      >
                        删除
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* 分页控件 */}
              {classicTotalPages > 1 && (
                <div className="pagination">
                  <button 
                    className="pagination-btn"
                    onClick={() => setClassicPage(1)}
                    disabled={classicPage === 1}
                  >
                    首页
                  </button>
                  <button 
                    className="pagination-btn"
                    onClick={() => setClassicPage(prev => Math.max(1, prev - 1))}
                    disabled={classicPage === 1}
                  >
                    上一页
                  </button>
                  <span className="pagination-info">
                    第 {classicPage} / {classicTotalPages} 页，共 {classicQuestions.length} 条
                  </span>
                  <button 
                    className="pagination-btn"
                    onClick={() => setClassicPage(prev => Math.min(classicTotalPages, prev + 1))}
                    disabled={classicPage === classicTotalPages}
                  >
                    下一页
                  </button>
                  <button 
                    className="pagination-btn"
                    onClick={() => setClassicPage(classicTotalPages)}
                    disabled={classicPage === classicTotalPages}
                  >
                    末页
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '24px', color: '#718096' }}>暂无经典题目数据</div>
          )}
        </div>
      </div>
    );
  };

  // 自定义Tooltip组件
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{`日期: ${label}`}</p>
          {payload.map((entry, index) => {
            // 如果是金额，显示4位小数
            const isMoney = entry.name && (entry.name.includes('费用') || entry.name.includes('金额') || entry.name.includes('润色') || entry.name.includes('生成') || entry.name.includes('输入'));
            const displayValue = isMoney && typeof entry.value === 'number' 
              ? `¥${entry.value.toFixed(4)}` 
              : entry.value.toLocaleString();
            return (
              <p key={index} className="tooltip-item" style={{ color: entry.color }}>
                {`${entry.name}: ${displayValue}`}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // 每日新增用户数趋势图
  const DailyUserChart = () => {
    // 优先使用后端返回的每日统计数据
    const backendDailyUserData = dailyStats && dailyStats.daily_users && dailyStats.daily_users.length
      ? dailyStats.daily_users.map(item => ({
        date: item.date.slice(5), // 显示 MM-DD
        users: item.new_users,
        cumulative: item.total_users
      }))
      : null;

    const rangeLength = dailyTimeRange === 'week' ? 7 : 30;
    const baseData = backendDailyUserData || dailyUserData;
    const sourceData = baseData.slice(-rangeLength);

    const currentDataKey = chartType === 'new' ? 'users' : 'cumulative';

    // 计算统计数据
    const stats = sourceData.reduce((acc, item) => {
      const value = item[currentDataKey];
      acc.total += value;
      acc.max = Math.max(acc.max, value);
      acc.min = acc.min === null ? value : Math.min(acc.min, value);
      return acc;
    }, { total: 0, max: 0, min: null });
    const avg = Math.round(stats.total / sourceData.length || 0);

    // 计算增长率（最后一天相比第一天）
    const firstValue = sourceData[0][currentDataKey];
    const lastValue = sourceData[sourceData.length - 1][currentDataKey];
    const growthRate = (firstValue === 0)
      ? '0.0'
      : (((lastValue - firstValue) / firstValue) * 100).toFixed(1);

    const paddingPercent = 0.2;
    const usersStats = sourceData.reduce((acc, item) => {
      const value = item['users'];
      acc.max = Math.max(acc.max, value);
      acc.min = acc.min === null ? value : Math.min(acc.min, value);
      return acc;
    }, { max: 0, min: null });
    const usersRange = usersStats.max - usersStats.min;
    const yMinLeft = Math.max(0, usersStats.min - usersRange * paddingPercent);
    const yMaxLeft = usersStats.max + usersRange * paddingPercent;

    const cumulativeStats = sourceData.reduce((acc, item) => {
      const value = item['cumulative'];
      acc.max = Math.max(acc.max, value);
      acc.min = acc.min === null ? value : Math.min(acc.min, value);
      return acc;
    }, { max: 0, min: null });
    const cumulativeRange = cumulativeStats.max - cumulativeStats.min;
    const yMinRight = Math.max(0, cumulativeStats.min - cumulativeRange * paddingPercent);
    const yMaxRight = cumulativeStats.max + cumulativeRange * paddingPercent;

    return (
      <div className="chart-container">
        <div className="chart-header">
          <div className="chart-controls">
            <select
              className="chart-select"
              value={chartType}
              onChange={(e) => setChartType(e.target.value)}
            >
              <option value="new">新增用户</option>
              <option value="cumulative">累计用户</option>
            </select>
          </div>
          <div className="chart-controls" style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              className={`range-btn ${dailyTimeRange === 'week' ? 'active' : ''}`}
              onClick={() => setDailyTimeRange('week')}
            >
              近7天
            </button>
            <button
              className={`range-btn ${dailyTimeRange === 'month' ? 'active' : ''}`}
              onClick={() => setDailyTimeRange('month')}
            >
              近30天
            </button>
            <button
              className="range-btn"
              onClick={handleRefreshDailyUsers}
              title="仅刷新每日用户趋势"
            >
              刷新
            </button>
          </div>
        </div>

        {/* 数据统计摘要 */}
        <div className="chart-stats-summary">
          <div className="chart-stat-item">
            <span className="stat-label">平均值:</span>
            <span className="stat-value">{avg.toLocaleString()}</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最高:</span>
            <span className="stat-value">{stats.max.toLocaleString()}</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最低:</span>
            <span className="stat-value">{stats.min.toLocaleString()}</span>
          </div>
          {chartType === 'new' && (
            <div className="chart-stat-item">
              <span className="stat-label">周期变化:</span>
              <span className={`stat-value ${parseFloat(growthRate) >= 0 ? 'positive' : 'negative'}`}>
                {parseFloat(growthRate) >= 0 ? '+' : ''}{growthRate}%
              </span>
            </div>
          )}
          {chartType === 'cumulative' && (
            <div className="chart-stat-item">
              <span className="stat-label">累计增长:</span>
              <span className="stat-value positive">
                +{(lastValue - firstValue).toLocaleString()}
              </span>
            </div>
          )}
        </div>

        <div className="chart-content">
          <ResponsiveContainer width="100%" height={180}>
            <ComposedChart data={sourceData} margin={{ top: 25, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="date"
                stroke="#718096"
                fontSize={8}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                yAxisId="left"
                stroke="#718096"
                fontSize={8}
                tickLine={false}
                axisLine={false}
                domain={[yMinLeft, yMaxLeft]}
                tickFormatter={(value) => value.toLocaleString()}
              >
                <Label
                  value="人数"
                  angle={-90}
                  position="insideLeft"
                  style={{ textAnchor: 'middle', fontSize: 8, fill: '#718096' }}
                />
              </YAxis>
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#718096"
                fontSize={8}
                tickLine={false}
                axisLine={false}
                domain={[yMinRight, yMaxRight]}
                tickFormatter={(value) => value.toLocaleString()}
              >
                <Label
                  value="总用户数"
                  angle={90}
                  position="insideRight"
                  style={{ textAnchor: 'middle', fontSize: 8, fill: '#718096' }}
                />
              </YAxis>
              <Tooltip content={<CustomTooltip />} />
              <Bar yAxisId="left" dataKey="users" name="新增用户" fill="#667eea" isAnimationActive={refreshingPanel === 'daily'} />
              <Area yAxisId="right" type="monotone" dataKey="cumulative" stroke="#667eea" fill="rgba(102,126,234,0.2)" name="累计用户" isAnimationActive={refreshingPanel === 'daily'} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // 付费趋势图
  const RevenueChart = () => {
    // 优先使用后端返回的每日收入统计
    const backendRevenueData = dailyStats && dailyStats.daily_revenue && dailyStats.daily_revenue.length
      ? dailyStats.daily_revenue.map(item => ({
        date: item.date.slice(5), // 显示 MM-DD
        revenue: item.revenue,
        users: item.paid_users
      }))
      : null;

    const rangeLength = revenueTimeRange === 'week' ? 7 : 30;
    const baseData = backendRevenueData || revenueData;
    const sourceData = baseData.slice(-rangeLength);

    const currentDataKey = revenueChartType === 'revenue' ? 'revenue' : 'users';
    const currentName = revenueChartType === 'revenue' ? '收入 (¥)' : '付费用户数';

    // 计算统计数据
    const stats = sourceData.reduce((acc, item) => {
      const value = item[currentDataKey];
      acc.total += value;
      acc.max = Math.max(acc.max, value);
      acc.min = acc.min === null ? value : Math.min(acc.min, value);
      return acc;
    }, { total: 0, max: 0, min: null });
    const avg = Math.round(stats.total / sourceData.length || 0);

    // 计算增长率
    const firstValue = sourceData[0][currentDataKey];
    const lastValue = sourceData[sourceData.length - 1][currentDataKey];
    const growthRate = (firstValue === 0)
      ? '0.0'
      : (((lastValue - firstValue) / firstValue) * 100).toFixed(1);

    // 计算总收入和总付费用户
    const revenueStats = sourceData.reduce((acc, item) => {
      acc.totalRevenue += item.revenue;
      acc.totalUsers += item.users;
      return acc;
    }, { totalRevenue: 0, totalUsers: 0 });

    // 计算Y轴范围，使其视觉高度与其他图表相似
    const dataRange = stats.max - stats.min;
    const paddingPercent = 0.2; // 上下各留20%的空白
    const yMin = Math.max(0, stats.min - dataRange * paddingPercent);
    const yMax = stats.max + dataRange * paddingPercent;

    return (
      <div className="chart-container">
        <div className="chart-header">
          <div className="chart-controls">
            <select
              className="chart-select"
              value={revenueChartType}
              onChange={(e) => setRevenueChartType(e.target.value)}
            >
              <option value="revenue">收入</option>
              <option value="users">付费用户</option>
            </select>
          </div>
          <div className="chart-controls" style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              className={`range-btn ${revenueTimeRange === 'week' ? 'active' : ''}`}
              onClick={() => setRevenueTimeRange('week')}
            >
              近7天
            </button>
            <button
              className={`range-btn ${revenueTimeRange === 'month' ? 'active' : ''}`}
              onClick={() => setRevenueTimeRange('month')}
            >
              近30天
            </button>
            <button
              className="range-btn"
              onClick={handleRefreshRevenue}
              title="仅刷新付费/收入趋势"
            >
              刷新
            </button>
          </div>
        </div>

        {/* 数据统计摘要 */}
        <div className="chart-stats-summary">
          <div className="chart-stat-item">
            <span className="stat-label">平均值:</span>
            <span className="stat-value">
              {revenueChartType === 'revenue' ? '¥' : ''}{avg.toLocaleString()}
              {revenueChartType === 'users' ? ' 人' : ''}
            </span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最高:</span>
            <span className="stat-value">
              {revenueChartType === 'revenue' ? '¥' : ''}{stats.max.toLocaleString()}
              {revenueChartType === 'users' ? ' 人' : ''}
            </span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最低:</span>
            <span className="stat-value">
              {revenueChartType === 'revenue' ? '¥' : ''}{stats.min.toLocaleString()}
              {revenueChartType === 'users' ? ' 人' : ''}
            </span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">周期变化:</span>
            <span className={`stat-value ${parseFloat(growthRate) >= 0 ? 'positive' : 'negative'}`}>
              {parseFloat(growthRate) >= 0 ? '+' : ''}{growthRate}%
            </span>
          </div>
          {revenueChartType === 'revenue' && (
            <div className="chart-stat-item">
              <span className="stat-label">周期总计:</span>
              <span className="stat-value">¥{revenueStats.totalRevenue.toLocaleString()}</span>
            </div>
          )}
          {revenueChartType === 'users' && (
            <div className="chart-stat-item">
              <span className="stat-label">周期总计:</span>
              <span className="stat-value">{revenueStats.totalUsers} 人</span>
            </div>
          )}
        </div>

        <div className="chart-content">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={sourceData} margin={{ top: 25, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="date"
                stroke="#718096"
                fontSize={8}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#718096"
                fontSize={8}
                tickLine={false}
                axisLine={false}
                domain={[yMin, yMax]}
                tickFormatter={(value) => value.toLocaleString()}
              >
                <Label
                  value={revenueChartType === 'revenue' ? '金额 (¥)' : '人数'}
                  angle={-90}
                  position="insideLeft"
                  style={{ textAnchor: 'middle', fontSize: 8, fill: '#718096' }}
                />
              </YAxis>
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey={currentDataKey} name={currentName} fill="#667eea" isAnimationActive={refreshingPanel === 'revenue'} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // 用户留存率趋势图
  const RetentionChart = () => {
    const retentionNames = {
      'next_day': '次日留存',
      'seven_day': '七日留存',
      'thirty_day': '三十日留存'
    };

    const currentName = retentionNames[retentionType];

    // 计算统计数据
    const stats = retentionRateData.reduce((acc, item) => {
      const value = item[retentionType];
      acc.total += value;
      acc.max = Math.max(acc.max, value);
      acc.min = acc.min === null ? value : Math.min(acc.min, value);
      return acc;
    }, { total: 0, max: 0, min: null });
    const avg = Math.round(stats.total / retentionRateData.length);

    // 计算增长率
    const firstValue = retentionRateData[0][retentionType];
    const lastValue = retentionRateData[retentionRateData.length - 1][retentionType];
    const growthRate = ((lastValue - firstValue) / firstValue * 100).toFixed(1);

    const avgRetention = Math.round(retentionRateData.reduce((sum, item) => sum + item[retentionType], 0) / retentionRateData.length);

    return (
      <div className="chart-container">
        <div className="chart-header">
          <div className="chart-controls">
            <select
              className="chart-select"
              value={retentionType}
              onChange={(e) => setRetentionType(e.target.value)}
            >
              <option value="next_day">次日留存</option>
              <option value="seven_day">七日留存</option>
              <option value="thirty_day">三十日留存</option>
            </select>
          </div>
        </div>

        <div className="chart-stats-summary">
          <div className="chart-stat-item">
            <span className="stat-label">平均值:</span>
            <span className="stat-value">{avg}%</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最高:</span>
            <span className="stat-value">{stats.max}%</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最低:</span>
            <span className="stat-value">{stats.min}%</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">周期变化:</span>
            <span className={`stat-value ${parseFloat(growthRate) >= 0 ? 'positive' : 'negative'}`}>
              {parseFloat(growthRate) >= 0 ? '+' : ''}{growthRate}%
            </span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">平均留存:</span>
            <span className="stat-value">{avgRetention}%</span>
          </div>
        </div>

        <div className="chart-content">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={retentionRateData} margin={{ top: 25, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#718096" fontSize={8} tickLine={false} axisLine={false} />
              <YAxis stroke="#718096" fontSize={8} tickLine={false} axisLine={false}>
                <Label value="留存率 (%)" angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fontSize: 8, fill: '#718096' }} />
              </YAxis>
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey={retentionType} stroke="#667eea" strokeWidth={3}
                dot={{ fill: '#667eea', strokeWidth: 2, r: 4 }} activeDot={{ r: 6, stroke: '#667eea', strokeWidth: 2 }}
                name={currentName} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // 用户活跃度趋势图
  const ActivityChart = () => {
    const activityNames = {
      'dau': '日活跃用户',
      'mau': '月活跃用户',
      'avg_session': '平均时长'
    };

    const currentName = activityNames[activityType];
    const unit = activityType === 'avg_session' ? ' 分钟' : '';

    // 计算统计数据
    const stats = activityData.reduce((acc, item) => {
      const value = item[activityType];
      acc.total += value;
      acc.max = Math.max(acc.max, value);
      acc.min = acc.min === null ? value : Math.min(acc.min, value);
      return acc;
    }, { total: 0, max: 0, min: null });
    const avg = Math.round(stats.total / activityData.length);

    const firstValue = activityData[0][activityType];
    const lastValue = activityData[activityData.length - 1][activityType];
    const growthRate = ((lastValue - firstValue) / firstValue * 100).toFixed(1);

    return (
      <div className="chart-container">
        <div className="chart-header">
          <div className="chart-controls">
            <select className="chart-select" value={activityType} onChange={(e) => setActivityType(e.target.value)}>
              <option value="dau">日活跃用户</option>
              <option value="mau">月活跃用户</option>
              <option value="avg_session">平均时长</option>
            </select>
          </div>
        </div>

        <div className="chart-stats-summary">
          <div className="chart-stat-item">
            <span className="stat-label">平均值:</span>
            <span className="stat-value">{avg.toLocaleString()}{unit}</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最高:</span>
            <span className="stat-value">{stats.max.toLocaleString()}{unit}</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最低:</span>
            <span className="stat-value">{stats.min.toLocaleString()}{unit}</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">周期变化:</span>
            <span className={`stat-value ${parseFloat(growthRate) >= 0 ? 'positive' : 'negative'}`}>
              {parseFloat(growthRate) >= 0 ? '+' : ''}{growthRate}%
            </span>
          </div>
        </div>

        <div className="chart-content">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={activityData} margin={{ top: 25, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#718096" fontSize={8} tickLine={false} axisLine={false} />
              <YAxis stroke="#718096" fontSize={8} tickLine={false} axisLine={false} tickFormatter={(value) => value.toLocaleString()}>
                <Label value="用户数/时长" angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fontSize: 8, fill: '#718096' }} />
              </YAxis>
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey={activityType} stroke="#667eea" strokeWidth={3}
                dot={{ fill: '#667eea', strokeWidth: 2, r: 4 }} activeDot={{ r: 6, stroke: '#667eea', strokeWidth: 2 }}
                name={currentName} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // 核心功能使用时长分析图
  const FeatureDurationChart = () => {
    const durationNames = {
      'mock_interview': '模拟面试',
      'meeting_assistant': '会议助手'
    };

    const currentName = durationNames[durationType];

    // 计算统计数据
    const stats = featureDurationData.reduce((acc, item) => {
      const value = item[durationType];
      acc.total += value;
      acc.max = Math.max(acc.max, value);
      acc.min = acc.min === null ? value : Math.min(acc.min, value);
      return acc;
    }, { total: 0, max: 0, min: null });
    const avg = (featureDurationData.length ? (stats.total / featureDurationData.length) : 0).toFixed(1);

    const firstValue = featureDurationData.length ? featureDurationData[0][durationType] : 0;
    const lastValue = featureDurationData.length ? featureDurationData[featureDurationData.length - 1][durationType] : 0;
    const growthRate = (firstValue === 0)
      ? '0.0'
      : (((lastValue - firstValue) / firstValue) * 100).toFixed(1);

    return (
      <div className="chart-container">
        <div className="chart-header">
          <div className="chart-controls">
            <select className="chart-select" value={durationType} onChange={(e) => setDurationType(e.target.value)}>
              <option value="mock_interview">模拟面试</option>
              <option value="meeting_assistant">会议助手</option>
            </select>
          </div>
          <div className="chart-controls" style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              className={`range-btn ${durationTimeRange === 'week' ? 'active' : ''}`}
              onClick={() => setDurationTimeRange('week')}
            >
              近7天
            </button>
            <button
              className={`range-btn ${durationTimeRange === 'month' ? 'active' : ''}`}
              onClick={() => setDurationTimeRange('month')}
            >
              近30天
            </button>
            <button
              className="range-btn"
              onClick={handleRefreshDuration}
              title="仅刷新平均会话时长趋势"
            >
              刷新
            </button>
          </div>
        </div>

        <div className="chart-stats-summary">
          <div className="chart-stat-item">
            <span className="stat-label">平均时长:</span>
            <span className="stat-value">{avg} 分钟/会话</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最高:</span>
            <span className="stat-value">{stats.max.toFixed(1)} 分钟</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最低:</span>
            <span className="stat-value">{stats.min.toFixed(1)} 分钟</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">周期变化:</span>
            <span className={`stat-value ${parseFloat(growthRate) >= 0 ? 'positive' : 'negative'}`}>
              {parseFloat(growthRate) >= 0 ? '+' : ''}{growthRate}%
            </span>
          </div>
        </div>

        <div className="chart-content">
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={(durationTimeRange === 'week' ? featureDurationData.slice(-7) : featureDurationData)} margin={{ top: 25, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#718096" fontSize={8} tickLine={false} axisLine={false} />
              <YAxis stroke="#718096" fontSize={8} tickLine={false} axisLine={false}>
                <Label value="使用时长 (分钟)" angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fontSize: 8, fill: '#718096' }} />
              </YAxis>
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey={durationType} stroke="#667eea" fill="rgba(102,126,234,0.2)" name={currentName} isAnimationActive={refreshingPanel === 'duration'} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const ApiCostTrendChart = () => {
    const rangeLength = apiCostTimeRange === 'week' ? 7 : 30;
    const baseData = apiCostData || [];
    const sourceData = baseData.slice(-rangeLength);
    const totals = sourceData.reduce((acc, d) => {
      acc.asr += d.asr || 0;
      acc.tts += d.tts || 0;
      acc.llm += d.llm || 0;
      return acc;
    }, { asr: 0, tts: 0, llm: 0 });
    const maxVal = Math.max(
      ...sourceData.map(d => Math.max(d.asr || 0, d.tts || 0, d.llm || 0)),
      0
    );
    const minVal = Math.min(
      ...sourceData.map(d => Math.min(d.asr || 0, d.tts || 0, d.llm || 0)),
      0
    );
    const paddingPercent = 0.2;
    const dataRange = maxVal - minVal;
    const yMin = Math.max(0, minVal - dataRange * paddingPercent);
    const yMax = maxVal + dataRange * paddingPercent;
    const handleRefresh = async () => {
      try {
        setRefreshingPanel('api_cost');
        await loadApiCostTrend(apiCostTimeRange === 'week' ? 7 : 30);
      } finally {
        setRefreshingPanel(null);
      }
    };
    return (
      <div className="chart-container">
        <div className="chart-header">
          <div className="chart-controls">
            <select
              className="chart-select"
              value={apiCostTimeRange}
              onChange={(e) => setApiCostTimeRange(e.target.value)}
            >
              <option value="week">近7天</option>
              <option value="month">近30天</option>
            </select>
          </div>
          <div className="chart-controls" style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              className="range-btn"
              onClick={handleRefresh}
              title="仅刷新API费用趋势"
            >
              刷新
            </button>
          </div>
        </div>
        <div className="chart-stats-summary">
          <div className="chart-stat-item">
            <span className="stat-label">ASR总费用:</span>
            <span className="stat-value">¥{totals.asr.toFixed(4)}</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">TTS总费用:</span>
            <span className="stat-value">¥{totals.tts.toFixed(4)}</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">LLM总费用:</span>
            <span className="stat-value">¥{totals.llm.toFixed(4)}</span>
          </div>
        </div>
        <div className="chart-content">
          <ResponsiveContainer width="100%" height={180}>
            <ComposedChart data={sourceData} margin={{ top: 25, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="date"
                stroke="#718096"
                fontSize={8}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#718096"
                fontSize={8}
                tickLine={false}
                axisLine={false}
                domain={[yMin, yMax]}
                tickFormatter={(value) => value.toLocaleString()}
              >
                <Label
                  value="金额 (¥)"
                  angle={-90}
                  position="insideLeft"
                  style={{ textAnchor: 'middle', fontSize: 8, fill: '#718096' }}
                />
              </YAxis>
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="asr" name="ASR" fill="#7C3AED" isAnimationActive={refreshingPanel === 'api_cost'} />
              <Bar dataKey="tts" name="TTS" fill="#14B8A6" isAnimationActive={refreshingPanel === 'api_cost'} />
              <Bar dataKey="llm" name="LLM" fill="#EF4444" isAnimationActive={refreshingPanel === 'api_cost'} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const ResumeGenerationCostTrendChart = () => {
    const rangeLength = resumeCostTimeRange === 'week' ? 7 : 30;
    const baseData = resumeCostData || [];
    const sourceData = baseData.slice(-rangeLength);
    const totals = sourceData.reduce((acc, d) => {
      acc.resume_generation += d.resume_generation || 0;
      acc.resume_assist_write += d.resume_assist_write || 0;
      acc.resume_voice_input += d.resume_voice_input || 0;
      acc.total += d.total || 0;
      return acc;
    }, { resume_generation: 0, resume_assist_write: 0, resume_voice_input: 0, total: 0 });
    const maxVal = Math.max(
      ...sourceData.map(d => Math.max(d.resume_generation || 0, d.resume_assist_write || 0, d.resume_voice_input || 0, d.total || 0)),
      0
    );
    const yMax = maxVal + maxVal * 0.2;
    const handleRefresh = async () => {
      try {
        setRefreshingPanel('resume_cost');
        await loadResumeGenerationCostTrend(resumeCostTimeRange === 'week' ? 7 : 30);
      } finally {
        setRefreshingPanel(null);
      }
    };
    return (
      <div className="chart-container">
        <div className="chart-header">
          <div className="chart-controls">
            <select
              className="chart-select"
              value={resumeCostTimeRange}
              onChange={(e) => setResumeCostTimeRange(e.target.value)}
            >
              <option value="week">近7天</option>
              <option value="month">近30天</option>
            </select>
          </div>
          <div className="chart-controls" style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              className="range-btn"
              onClick={handleRefresh}
              title="仅刷新简历生成费用趋势"
            >
              刷新
            </button>
          </div>
        </div>
        <div className="chart-stats-summary">
          <div className="chart-stat-item">
            <span className="stat-label">AI一键生成:</span>
            <span className="stat-value">¥{totals.resume_generation.toFixed(4)}</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">简历润色:</span>
            <span className="stat-value">¥{totals.resume_assist_write.toFixed(4)}</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">语音输入:</span>
            <span className="stat-value">¥{totals.resume_voice_input.toFixed(4)}</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">总费用:</span>
            <span className="stat-value">¥{totals.total.toFixed(4)}</span>
          </div>
        </div>
        <div className="chart-content">
          <ResponsiveContainer width="100%" height={180}>
            <ComposedChart data={sourceData} margin={{ top: 25, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="date"
                stroke="#718096"
                fontSize={8}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#718096"
                fontSize={8}
                tickLine={false}
                axisLine={false}
                domain={[0, yMax]}
                tickFormatter={(value) => value.toLocaleString()}
              >
                <Label
                  value="金额 (¥)"
                  angle={-90}
                  position="insideLeft"
                  style={{ textAnchor: 'middle', fontSize: 8, fill: '#718096' }}
                />
              </YAxis>
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="resume_generation" name="AI一键生成简历" fill="#6366F1" isAnimationActive={refreshingPanel === 'resume_cost'} />
              <Bar dataKey="resume_assist_write" name="简历润色" fill="#10B981" isAnimationActive={refreshingPanel === 'resume_cost'} />
              <Bar dataKey="resume_voice_input" name="语音输入" fill="#F59E0B" isAnimationActive={refreshingPanel === 'resume_cost'} />
              <Line type="monotone" dataKey="total" name="总费用" stroke="#EF4444" strokeWidth={2} dot={false} isAnimationActive={refreshingPanel === 'resume_cost'} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // API性能监控图表
  const ApiPerformanceChart = () => {
    const hasData = apiPerformance.total_calls > 0;
    const avgSeconds = apiPerformance.avg_duration_seconds || 0;
    const maxSeconds = apiPerformance.max_duration_seconds || 0;

    // 获取时间窗口显示文本
    const getWindowText = (minutes) => {
      if (minutes === 60) return '近1小时';
      if (minutes === 180) return '近3小时';
      if (minutes === 360) return '近6小时';
      if (minutes === 720) return '近12小时';
      if (minutes === 1440) return '近24小时';
      return `近${minutes}分钟`;
    };

    // 根据API类型获取时长含义
    const getDurationLabel = (apiType) => {
      const type = apiType.toLowerCase();
      if (type === 'llm' || type === 'face_detection') {
        return '响应时间';
      } else if (type === 'asr') {
        return '音频时长';
      } else if (type === 'tts') {
        return '字符处理';
      }
      return '处理时长';
    };

    // 准备按类型分组的数据
    const byTypeData = Object.entries(apiPerformance.by_type || {}).map(([type, data]) => ({
      type: type.toUpperCase(),
      typeLower: type.toLowerCase(),
      calls: data.calls || 0,
      avg_duration: data.avg_duration_seconds || 0,
      max_duration: data.max_duration_seconds || 0,
      total_cost: data.total_cost || 0,
      durationLabel: getDurationLabel(type)
    }));

    // 自定义Tooltip显示不同API类型的含义
    const ApiPerformanceTooltip = ({ active, payload }) => {
      if (!active || !payload || !payload.length) return null;
      const data = payload[0].payload;
      return (
        <div style={{
          background: 'rgba(255, 255, 255, 0.96)',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#2d3748' }}>
            {data.type}
          </div>
          <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px' }}>
            调用次数: <span style={{ fontWeight: 'bold' }}>{data.calls}</span>
          </div>
          <div style={{ fontSize: '12px', color: '#48bb78', marginBottom: '4px' }}>
            平均{data.durationLabel}: <span style={{ fontWeight: 'bold' }}>{data.avg_duration.toFixed(2)}s</span>
          </div>
          <div style={{ fontSize: '12px', color: '#f56565' }}>
            最大{data.durationLabel}: <span style={{ fontWeight: 'bold' }}>{data.max_duration.toFixed(2)}s</span>
          </div>
        </div>
      );
    };

    return (
      <div className="chart-container">
        <div className="chart-header">
          <div className="chart-controls">
            <select
              className="chart-select"
              value={apiPerformanceWindow}
              onChange={(e) => setApiPerformanceWindow(Number(e.target.value))}
            >
              <option value={60}>近1小时</option>
              <option value={180}>近3小时</option>
              <option value={360}>近6小时</option>
              <option value={720}>近12小时</option>
              <option value={1440}>近24小时</option>
            </select>
          </div>
          <div className="chart-controls" style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              className="range-btn"
              onClick={handleRefreshApiPerformance}
              title="刷新API性能数据"
              disabled={refreshingPanel === 'apiPerformance'}
            >
              {refreshingPanel === 'apiPerformance' ? '刷新中...' : '刷新'}
            </button>
          </div>
        </div>

        <div className="chart-stats-summary">
          <div className="chart-stat-item">
            <span className="stat-label">总调用次数:</span>
            <span className="stat-value">{apiPerformance.total_calls.toLocaleString()}</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">有效数据:</span>
            <span className="stat-value">{apiPerformance.calls_with_duration.toLocaleString()}</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">平均响应时间:</span>
            <span className="stat-value">{avgSeconds.toFixed(2)}s</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最大响应时间:</span>
            <span className="stat-value">{maxSeconds.toFixed(2)}s</span>
          </div>
        </div>

        {hasData && byTypeData.length > 0 ? (
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={byTypeData} margin={{ top: 25, right: 20, left: 0, bottom: 5 }} barGap={6}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="type"
                  stroke="#718096"
                  fontSize={11}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tick={{ fill: '#4a5568', fontWeight: 500 }}
                  dy={10}
                />
                <YAxis
                  yAxisId="left"
                  stroke="#718096"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  tickCount={5}
                >
                  <Label
                    value="调用次数"
                    angle={-90}
                    position="insideLeft"
                    style={{ textAnchor: 'middle', fontSize: 10, fill: '#718096' }}
                  />
                </YAxis>
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#718096"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  tickCount={5}
                >
                  <Label
                    value="时长 (s)"
                    angle={90}
                    position="insideRight"
                    style={{ textAnchor: 'middle', fontSize: 10, fill: '#718096' }}
                  />
                </YAxis>
                <Tooltip content={<ApiPerformanceTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }} />
                <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
                <Bar
                  yAxisId="left"
                  dataKey="calls"
                  name="调用次数"
                  fill="#6366f1"
                  radius={[4, 4, 0, 0]}
                  barSize={20}
                  isAnimationActive={refreshingPanel === 'apiPerformance'}
                />
                <Bar
                  yAxisId="right"
                  dataKey="avg_duration"
                  name="平均时长(s)"
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                  barSize={20}
                  isAnimationActive={refreshingPanel === 'apiPerformance'}
                />
                <Bar
                  yAxisId="right"
                  dataKey="max_duration"
                  name="最大时长(s)"
                  fill="#f43f5e"
                  radius={[4, 4, 0, 0]}
                  barSize={20}
                  isAnimationActive={refreshingPanel === 'apiPerformance'}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#a0aec0' }}>
            {getWindowText(apiPerformanceWindow)}内暂无API调用数据
          </div>
        )}
      </div>
    );
  };

  // 转化率趋势图
  const ConversionChart = () => {
    const conversionNames = {
      'register_to_active': '注册到激活',
      'visit_to_use': '访问到使用',
      'free_to_paid': '免费到付费'
    };

    const currentName = conversionNames[conversionType];

    // 计算统计数据
    const stats = conversionData.reduce((acc, item) => {
      const value = item[conversionType];
      acc.total += value;
      acc.max = Math.max(acc.max, value);
      acc.min = acc.min === null ? value : Math.min(acc.min, value);
      return acc;
    }, { total: 0, max: 0, min: null });
    const avg = (stats.total / conversionData.length).toFixed(1);

    const firstValue = conversionData[0][conversionType];
    const lastValue = conversionData[conversionData.length - 1][conversionType];
    const growthRate = ((lastValue - firstValue) / firstValue * 100).toFixed(1);

    return (
      <div className="chart-container">
        <div className="chart-header">
          <div className="chart-controls">
            <select className="chart-select" value={conversionType} onChange={(e) => setConversionType(e.target.value)}>
              <option value="register_to_active">注册到激活</option>
              <option value="visit_to_use">访问到使用</option>
              <option value="free_to_paid">免费到付费</option>
            </select>
          </div>
        </div>

        <div className="chart-stats-summary">
          <div className="chart-stat-item">
            <span className="stat-label">平均值:</span>
            <span className="stat-value">{avg}%</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最高:</span>
            <span className="stat-value">{stats.max.toFixed(1)}%</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最低:</span>
            <span className="stat-value">{stats.min.toFixed(1)}%</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">周期变化:</span>
            <span className={`stat-value ${parseFloat(growthRate) >= 0 ? 'positive' : 'negative'}`}>
              {parseFloat(growthRate) >= 0 ? '+' : ''}{growthRate}%
            </span>
          </div>
        </div>

        <div className="chart-content">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={conversionData} margin={{ top: 25, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#718096" fontSize={8} tickLine={false} axisLine={false} />
              <YAxis stroke="#718096" fontSize={8} tickLine={false} axisLine={false}>
                <Label value="转化率 (%)" angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fontSize: 8, fill: '#718096' }} />
              </YAxis>
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey={conversionType} stroke="#667eea" strokeWidth={3}
                dot={{ fill: '#667eea', strokeWidth: 2, r: 4 }} activeDot={{ r: 6, stroke: '#667eea', strokeWidth: 2 }}
                name={currentName} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Legacy chart components are intentionally kept for quick rollback/debug.
  // Use void to mark references and avoid no-unused-vars warnings.
  void RetentionChart;
  void ActivityChart;
  void ConversionChart;

  // 功能使用趋势图
  const ModuleUsageChart = () => {
    const moduleNames = {
      'job_search': '找工作',
      'resume_diagnosis': '简历诊断',
      'interview_questions': '面试押题',
      'mock_interview': '模拟面试',
      'meeting_assistant': '会议助手',
      'resume_generation': 'AI一键生成简历',
      'resume_assist_write': '简历润色',
      'resume_pdf_export': '简历导出',
      'resume_voice_input': '语音输入',
      'resume_export_points': '导出积分消耗'
    };

    const currentModuleName = moduleNames[moduleChartType];

    // 计算统计数据
    const stats = moduleUsageData.reduce((acc, item) => {
      const value = item[moduleChartType];
      acc.total += value;
      acc.max = Math.max(acc.max, value);
      acc.min = acc.min === null ? value : Math.min(acc.min, value);
      return acc;
    }, { total: 0, max: 0, min: null });
    const avg = Math.round(moduleUsageData.length ? (stats.total / moduleUsageData.length) : 0);

    // 计算增长率
    const firstValue = moduleUsageData.length ? moduleUsageData[0][moduleChartType] : 0;
    const lastValue = moduleUsageData.length ? moduleUsageData[moduleUsageData.length - 1][moduleChartType] : 0;
    const growthRate = (firstValue === 0)
      ? '0.0'
      : (((lastValue - firstValue) / firstValue) * 100).toFixed(1);

    // 计算总使用次数
    const totalUsage = moduleUsageData.reduce((sum, item) => sum + (item[moduleChartType] || 0), 0);

    // 计算Y轴范围，使其视觉高度与其他图表相似
    const dataRange = stats.max - stats.min;
    const paddingPercent = 0.2; // 上下各留20%的空白
    const yMin = Math.max(0, stats.min - dataRange * paddingPercent);
    const yMax = stats.max + dataRange * paddingPercent;

    return (
      <div className="chart-container">
        <div className="chart-header">
          <div className="chart-controls">
            <select
              className="chart-select"
              value={moduleChartType}
              onChange={(e) => setModuleChartType(e.target.value)}
            >
              <option value="job_search">找工作</option>
              <option value="resume_diagnosis">简历诊断</option>
              <option value="interview_questions">面试押题</option>
              <option value="mock_interview">模拟面试</option>
              <option value="meeting_assistant">会议助手</option>
              <option value="resume_generation">AI一键生成简历</option>
              <option value="resume_assist_write">简历润色</option>
              <option value="resume_pdf_export">简历导出</option>
              <option value="resume_voice_input">语音输入</option>
              <option value="resume_export_points">导出积分消耗</option>
            </select>
          </div>
          <div className="chart-controls" style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              className={`range-btn ${moduleTimeRange === 'week' ? 'active' : ''}`}
              onClick={() => setModuleTimeRange('week')}
            >
              近7天
            </button>
            <button
              className={`range-btn ${moduleTimeRange === 'month' ? 'active' : ''}`}
              onClick={() => setModuleTimeRange('month')}
            >
              近30天
            </button>
            <button
              className="range-btn"
              onClick={handleRefreshModuleUsage}
              title="仅刷新功能使用趋势"
            >
              刷新
            </button>
          </div>
        </div>

        {/* 数据统计摘要 */}
        <div className="chart-stats-summary">
          <div className="chart-stat-item">
            <span className="stat-label">平均值:</span>
            <span className="stat-value">{avg.toLocaleString()} 次</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最高:</span>
            <span className="stat-value">{stats.max.toLocaleString()} 次</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">最低:</span>
            <span className="stat-value">{stats.min.toLocaleString()} 次</span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">周期变化:</span>
            <span className={`stat-value ${parseFloat(growthRate) >= 0 ? 'positive' : 'negative'}`}>
              {parseFloat(growthRate) >= 0 ? '+' : ''}{growthRate}%
            </span>
          </div>
          <div className="chart-stat-item">
            <span className="stat-label">周期总计:</span>
            <span className="stat-value">{totalUsage.toLocaleString()} 次</span>
          </div>
        </div>

        <div className="chart-content">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={(moduleTimeRange === 'week' ? moduleUsageData.slice(-7) : moduleUsageData)} margin={{ top: 25, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="date"
                stroke="#718096"
                fontSize={8}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#718096"
                fontSize={8}
                tickLine={false}
                axisLine={false}
                domain={[yMin, yMax]}
                tickFormatter={(value) => value.toLocaleString()}
              >
                <Label
                  value="使用次数"
                  angle={-90}
                  position="insideLeft"
                  style={{ textAnchor: 'middle', fontSize: 8, fill: '#718096' }}
                />
              </YAxis>
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey={moduleChartType} name={currentModuleName} fill="#667eea" isAnimationActive={refreshingPanel === 'module'} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };



  const renderDashboard = () => {
    return (
      <div className="dashboard-content">
        <div className="dashboard-header-with-selector">
          <div>
            <h3>运营数据概览</h3>
            <p className="header-desc">全面的平台运营数据和用户行为分析</p>
          </div>
        </div>

        <div className="stats-section">
          <h4 className="section-subtitle">核心指标</h4>
          <div className="stats-grid">
            <StatCard
              title="总用户数"
              value={dashboardStats.total_users.toLocaleString()}
            />
            <StatCard
              title="今日新增"
              value={dashboardStats.today_new_users}
            />
            <StatCard
              title="付费用户"
              value={dashboardStats.paid_users}
            />
            <StatCard
              title="在线人数"
              value={analyticsStats.users.online}
            />
            <StatCard
              title="日活用户"
              value={dashboardStats.daily_active_users}
            />
            <StatCard
              title="月活用户"
              value={dashboardStats.monthly_active_users}
            />
          </div>
        </div>

        <div className="revenue-section">
          <h4 className="section-subtitle">收入数据</h4>
          <div className="revenue-grid">
            <div className="revenue-card">
              <div className="revenue-label">总收入</div>
              <div className="revenue-value">¥ {analyticsStats.revenue.total.toLocaleString()}</div>
            </div>
            <div className="revenue-card">
              <div className="revenue-label">今日收入</div>
              <div className="revenue-value">¥ {analyticsStats.revenue.today.toLocaleString()}</div>
            </div>
            <div className="revenue-card">
              <div className="revenue-label">本周收入</div>
              <div className="revenue-value">¥ {analyticsStats.revenue.week.toLocaleString()}</div>
            </div>
            <div className="revenue-card">
              <div className="revenue-label">本月收入</div>
              <div className="revenue-value">¥ {analyticsStats.revenue.month.toLocaleString()}</div>
            </div>
          </div>
        </div>

        <div className="stats-section">
          <h4 className="section-subtitle">简历生成运营指标</h4>
          <div className="stats-grid">
            <StatCard
              title="AI一键生成简历"
              value={resumeGenerationOverview.resume_generation_total.toLocaleString()}
            />
            <StatCard
              title="简历润色"
              value={resumeGenerationOverview.resume_assist_write_total.toLocaleString()}
            />
            <StatCard
              title="简历导出"
              value={resumeGenerationOverview.resume_pdf_export_total.toLocaleString()}
            />
            <StatCard
              title="语音输入"
              value={resumeGenerationOverview.resume_voice_input_total.toLocaleString()}
            />
            <StatCard
              title="导出积分消耗"
              value={resumeGenerationOverview.resume_export_points_total.toLocaleString()}
            />
          </div>
        </div>

        <div className="charts-section">
          <div className="charts-section-header">
            <h4 className="section-subtitle">数据趋势</h4>
          </div>
          <div className="charts-grid">
            <div className="chart-wrapper">
              <div className="chart-title-above">每日用户数趋势图</div>
              <DailyUserChart />
            </div>
            <div className="chart-wrapper">
              <div className="chart-title-above">收入与付费趋势图</div>
              <RevenueChart />
            </div>
          </div>
          <div className="charts-grid" style={{ marginTop: 24 }}>
            <div className="chart-wrapper">
              <div className="chart-title-above">功能平均会话时长（分钟）</div>
              <FeatureDurationChart />
            </div>
            <div className="chart-wrapper">
              <div className="chart-title-above">功能使用次数趋势</div>
              <ModuleUsageChart />
            </div>
          </div>
          <div className="charts-grid" style={{ marginTop: 24 }}>
            <div className="chart-wrapper">
              <div className="chart-title-above">API调用费用趋势</div>
              <ApiCostTrendChart />
            </div>
            <div className="chart-wrapper">
              <div className="chart-title-above">简历生成费用趋势</div>
              <ResumeGenerationCostTrendChart />
            </div>
          </div>
          <div className="charts-grid" style={{ marginTop: 24 }}>
            <div className="chart-wrapper" style={{ gridColumn: '1 / -1' }}>
              <div className="chart-title-above">API性能监控</div>
              <ApiPerformanceChart />
            </div>
          </div>
          <div style={{ marginTop: 24 }}>
            <h4 className="section-subtitle">指标说明</h4>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, color: '#4a5568', fontSize: 13 }}>
              <div style={{ marginBottom: 8 }}>- 已展示后端真实数据：每日新增/累计用户、每日收入与付费用户。</div>
              <div style={{ marginBottom: 8 }}>- 新增：功能使用次数趋势（找工作/简历诊断/面试押题/模拟面试/会议助手）。</div>
              <div style={{ marginBottom: 8 }}>- 新增：模拟面试与会议助手的平均会话时长（分钟）。</div>
              <div style={{ marginBottom: 8 }}>- 新增：API调用费用趋势（ASR/TTS/LLM）。</div>
              <div style={{ marginBottom: 8 }}>- 新增：简历生成费用趋势（AI一键生成简历/简历润色/语音输入及总费用）。</div>
              <div style={{ marginBottom: 8 }}>- 新增：简历生成运营指标（AI一键生成简历/简历润色/简历导出/语音输入/导出积分消耗）。</div>
              <div style={{ marginBottom: 8 }}>- 新增：API性能监控（调用次数、时长统计、按类型分组）。注：LLM/人脸检测显示API响应时间，ASR显示音频时长，TTS显示字符处理时长。</div>
              <div>- 转化率、留存与其他高级指标需进一步埋点和统一口径，后续迭代再开放。</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 渲染用户管理
  const renderUserManagement = () => (
    <div className="user-management">
      <h3>用户查询与管理</h3>
      <div className="search-section">
        <div className="search-bar-new">
          <div className="search-field-group">
            <label className="search-label">用户账号:</label>
            <input
              type="text"
              value={searchFilters.nickname}
              onChange={(e) => setSearchFilters({ ...searchFilters, nickname: e.target.value })}
              placeholder="请输入用户账号"
              className="search-input-new"
              onKeyPress={(e) => e.key === 'Enter' && handleUserSearch()}
            />
          </div>
          <div className="search-field-group">
            <label className="search-label">邮箱:</label>
            <input
              type="text"
              value={searchFilters.email}
              onChange={(e) => setSearchFilters({ ...searchFilters, email: e.target.value })}
              placeholder="请输入邮箱"
              className="search-input-new"
              onKeyPress={(e) => e.key === 'Enter' && handleUserSearch()}
            />
          </div>
          <div className="search-field-group">
            <label className="search-label">手机:</label>
            <input
              type="text"
              value={searchFilters.phone}
              onChange={(e) => setSearchFilters({ ...searchFilters, phone: e.target.value })}
              placeholder="请输入手机"
              className="search-input-new"
              onKeyPress={(e) => e.key === 'Enter' && handleUserSearch()}
            />
          </div>
          <div className="search-field-group">
            <label className="search-label">用户等级:</label>
            <select
              value={searchFilters.memberType}
              onChange={(e) => setSearchFilters({ ...searchFilters, memberType: e.target.value })}
              className="search-select-new"
            >
              <option value="">全部</option>
              <option value="member">会员 (L1-L9)</option>
              <option value="regular">普通用户 (L0)</option>
            </select>
          </div>
          <div className="search-actions">
            <button
              className="btn-search-new btn-search-query"
              onClick={handleUserSearch}
              disabled={isSearching}
            >
              查询
            </button>
            <button
              className="btn-search-new btn-search-reset"
              onClick={handleClearSearch}
            >
              重置
            </button>
          </div>
        </div>
      </div>

      {users.length > 0 && (
        <div className="user-results">
          <div className="table-section-header">
            <h4>{(searchFilters.nickname || searchFilters.email || searchFilters.phone || searchFilters.memberType) ? '搜索结果' : '用户列表'}</h4>
            <div className="table-section-info">
              共 {totalUsers} 条记录 {!(searchFilters.nickname || searchFilters.email || searchFilters.phone || searchFilters.memberType) && `（第 ${userPage} 页，每页 ${userPageSize} 条）`}
            </div>
          </div>
          <div className="user-table">
            <div className="user-table-header">
              <div className="col-index">序号</div>
              <div className="col-user-info">用户信息</div>
              <div className="col-contact">联系方式</div>
              <div className="col-points" style={{ cursor: 'pointer' }} onClick={() => handleSort('points')}>
                积分
                {searchFilters.sortBy === 'points' && (
                  <span style={{ marginLeft: 4 }}>{searchFilters.sortOrder === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
              <div className="col-points-change" style={{ cursor: 'pointer' }} onClick={() => handleSort('points_change')}>
                积分变化
                {searchFilters.sortBy === 'points_change' && (
                  <span style={{ marginLeft: 4 }}>{searchFilters.sortOrder === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
              <div className="col-level">等级</div>
              <div className="col-status">状态</div>
              <div className="col-time">注册时间</div>
              <div className="col-actions">操作</div>
            </div>
            {users.map((user, index) => (
              <div key={user.id} className="user-table-row">
                <div className="col-index">{(searchFilters.nickname || searchFilters.email || searchFilters.phone || searchFilters.memberType) ? index + 1 : (userPage - 1) * userPageSize + index + 1}</div>
                <div className="col-user-info">
                  <div className="user-nickname" title={user.nickname}>{user.nickname}</div>
                  <div className="user-id" title={user.user_id}>{user.user_id}</div>
                </div>
                <div className="col-contact">
                  <div className="user-email" title={user.email}>{user.email || '-'}</div>
                  <div className="user-phone" title={user.phone}>{user.phone || '-'}</div>
                </div>
                <div className="col-points">{user.point}</div>
                <div className={`col-points-change ${typeof user.latest_points_change === 'number' ? (user.latest_points_change > 0 ? 'points-change-positive' : user.latest_points_change < 0 ? 'points-change-negative' : '') : ''}`}>
                  <div className="points-change-value">{formatLatestPointsChange(user.latest_points_change)}</div>
                  <div className="points-change-time">{formatCompactDateTime(user.latest_points_change_at)}</div>
                </div>
                <div className="col-level">{user.level}</div>
                <div className="col-status">
                  <span className={`status-badge status-${user.status}`}>
                    {user.status === 'normal' ? '正常' : user.status === 'frozen' ? '已冻结' : '已拉黑'}
                  </span>
                </div>
                <div className="col-time">{new Date(user.created_at).toLocaleDateString().replace(/\//g, '-')}</div>
                <div className="col-actions">
                  <button
                    className="btn-table btn-secondary"
                    onClick={() => openUserDetailsModal(user)}
                    title="查看用户详情"
                  >
                    详情
                  </button>
                  <select
                    className="user-action-select"
                    defaultValue=""
                    onChange={(e) => {
                      const action = e.target.value;
                      e.target.value = '';
                      handleUserActionSelect(user, action);
                    }}
                  >
                    <option value="" disabled>更多操作</option>
                    <option value="points">调整积分</option>
                    {user.status === 'normal' && <option value="freeze">冻结账号</option>}
                    {user.status === 'normal' && <option value="blacklist">拉黑用户</option>}
                    {user.status === 'frozen' && <option value="unfreeze">解冻账号</option>}
                    {user.status === 'blacklist' && <option value="remove_blacklist">移出黑名单</option>}
                    {user.level === 'L0' && <option value="upgrade_member">升级会员</option>}
                  </select>
                </div>
              </div>
            ))}
          </div>

          {/* 分页组件 */}
          {totalUsers > userPageSize && (
            <div className="pagination">
              <button
                className="page-btn"
                onClick={() => {
                  setUserPage(1);
                }}
                disabled={userPage === 1}
              >
                首页
              </button>
              <button
                className="page-btn"
                onClick={() => {
                  setUserPage(userPage - 1);
                }}
                disabled={userPage === 1}
              >
                上一页
              </button>

              <span className="page-info">
                {userPage} / {Math.ceil(totalUsers / userPageSize)}
              </span>

              <button
                className="page-btn"
                onClick={() => {
                  setUserPage(userPage + 1);
                }}
                disabled={userPage >= Math.ceil(totalUsers / userPageSize)}
              >
                下一页
              </button>
              <button
                className="page-btn"
                onClick={() => {
                  setUserPage(Math.ceil(totalUsers / userPageSize));
                }}
                disabled={userPage >= Math.ceil(totalUsers / userPageSize)}
              >
                末页
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="operation-dashboard">
      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => {
            if (activeTab === 'dashboard') loadDashboardStats();
            else if (activeTab === 'content') loadNewsList();
            else if (activeTab === 'classic') loadClassicQuestions();
          }}>重试</button>
        </div>
      )}
      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'users' && renderUserManagement()}
      {activeTab === 'classic' && renderClassicManagement()}

      {activeTab === 'content' && (
        <div className="content-management">
          <div className="content-header">
            <h3>内容发布管理</h3>
          </div>

          {/* 统计卡片 */}
          <div className="stats-grid">
            <StatCard
              title="总动态数"
              value={stats.total}
            />
            <StatCard
              title="已发布"
              value={stats.published}
            />
            <StatCard
              title="草稿"
              value={stats.draft}
            />
            <StatCard
              title="已归档"
              value={stats.archived}
            />
          </div>

          {/* 动态列表 */}
          <div className="news-section">
            <div className="table-section-header">
              <h4>动态列表</h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div className="search-section" style={{ padding: 12 }}>
                  <div className="search-bar-new" style={{ gap: 12 }}>
                    <div className="search-field-group">
                      <label className="search-label">关键词</label>
                      <input
                        className="search-input-new"
                        type="text"
                        value={newsSearch}
                        onChange={(e) => setNewsSearch(e.target.value)}
                        placeholder="输入标题/内容/摘要关键词"
                        onKeyDown={(e) => { if (e.key === 'Enter') handleNewsSearch(); }}
                      />
                    </div>
                    <div className="search-actions">
                      <button className="btn-search-new btn-search-query" onClick={handleNewsSearch}>
                        查询
                      </button>
                      <button className="btn-search-new btn-search-reset" onClick={handleClearNewsSearch}>
                        清空
                      </button>
                    </div>
                  </div>
                </div>
                <div className="table-section-info">
                  共 {newsTotal} 条记录
                </div>
                <button className="btn btn-primary" onClick={() => {
                  resetForm();
                  setEditingNews(null);
                  setShowCreateModal(true);
                }}>
                  <span>创建动态</span>
                </button>
              </div>
            </div>
            {loading ? (
              <div className="loading">加载中...</div>
            ) : newsList.length > 0 ? (
              <div className="news-table">
                <div className="news-table-header">
                  <div className="news-col-id">序号</div>
                  <div className="news-col-title">标题</div>
                  <div className="news-col-category">分类</div>
                  <div className="news-col-status">状态</div>
                  <div className="news-col-featured">置顶</div>
                  <div className="news-col-views">浏览量</div>
                  <div className="news-col-time">时间</div>
                  <div className="news-col-actions">操作</div>
                </div>
                {newsList.map((news, index) => (
                  <div key={news.id} className="news-table-row">
                    <div className="news-col-id">{(newsPage - 1) * newsPageSize + index + 1}</div>
                    <div className="news-col-title">{news.title}</div>
                    <div className="news-col-category">{getCategoryText(news.category)}</div>
                    <div className="news-col-status">
                      {getStatusBadge(news.status)}
                    </div>
                    <div className="news-col-featured">
                      {news.is_featured ? <span className="featured-badge">置顶</span> : '-'}
                    </div>
                    <div className="news-col-views">{news.view_count}</div>
                    <div className="news-col-time">
                      {news.published_at ?
                        new Date(news.published_at).toLocaleString() :
                        new Date(news.created_at).toLocaleString()
                      }
                    </div>
                    <div className="news-col-actions">
                      <button
                        className="btn-table btn-secondary"
                        onClick={() => openEditModal(news)}
                      >
                        编辑
                      </button>
                      {news.status === 'draft' && (
                        <button
                          className="btn-table btn-success"
                          onClick={() => handlePublishNews(news.news_id)}
                        >
                          发布
                        </button>
                      )}
                      {news.status === 'published' && (
                        <button
                          className="btn-table btn-warning"
                          onClick={() => handleArchiveNews(news.news_id)}
                        >
                          归档
                        </button>
                      )}
                      <button
                        className="btn-table btn-danger"
                        onClick={() => handleDeleteNews(news.news_id)}
                      >
                        删除
                      </button>
                    </div>
                  </div>
                ))}
                {newsTotal > newsPageSize && (
                  <div className="pagination">
                    <button className="page-btn" onClick={handleNewsPrevPage} disabled={newsPage <= 1}>上一页</button>
                    <span className="page-info">第 {newsPage} / {newsTotalPages} 页 · 每页 {newsPageSize} 条</span>
                    <button className="page-btn" onClick={handleNewsNextPage} disabled={newsPage >= newsTotalPages}>下一页</button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>暂无动态数据</div>
            )}
          </div>
        </div>
      )}

      {showClassicModal && (
        <>
          <div
            className="sidebar-overlay"
            onClick={() => {
              setShowClassicModal(false);
              setEditingQuestion(null);
              resetClassicForm();
            }}
          ></div>
          <div className="sidebar-panel">
            <div className="sidebar-header">
              <h3>{editingQuestion ? '编辑经典题目' : '创建经典题目'}</h3>
              <button
                className="sidebar-close"
                onClick={() => {
                  setShowClassicModal(false);
                  setEditingQuestion(null);
                  resetClassicForm();
                }}
              >
                ×
              </button>
            </div>
            <div className="sidebar-body">
              <div className="form-group">
                <label>问题正文</label>
                <textarea
                  value={classicForm.question}
                  onChange={(e) =>
                    setClassicForm({ ...classicForm, question: e.target.value })
                  }
                  rows={4}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>分类</label>
                  <input
                    type="text"
                    value={classicForm.category}
                    onChange={(e) =>
                      setClassicForm({
                        ...classicForm,
                        category: e.target.value,
                      })
                    }
                    placeholder="例如：履历、动机、项目"
                  />
                </div>
                <div className="form-group">
                  <label>难度</label>
                  <select
                    value={classicForm.difficulty}
                    onChange={(e) =>
                      setClassicForm({
                        ...classicForm,
                        difficulty: e.target.value,
                      })
                    }
                  >
                    <option value="easy">简单</option>
                    <option value="medium">中等</option>
                    <option value="hard">困难</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>语言</label>
                  <select
                    value={classicForm.language}
                    onChange={(e) =>
                      setClassicForm({
                        ...classicForm,
                        language: e.target.value,
                      })
                    }
                  >
                    <option value="zh">中文</option>
                    <option value="en">英文</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>标签</label>
                <input
                  type="text"
                  value={classicForm.tagsText}
                  onChange={(e) =>
                    setClassicForm({
                      ...classicForm,
                      tagsText: e.target.value,
                    })
                  }
                  placeholder="多个标签用逗号分隔"
                />
              </div>
              <div className="form-group">
                <label>作答指导</label>
                <MarkdownEditor
                  value={classicForm.guidance}
                  onChange={(value) =>
                    setClassicForm({
                      ...classicForm,
                      guidance: value,
                    })
                  }
                  rows={8}
                />
              </div>
              <div className="form-group">
                <label>示例回答</label>
                <MarkdownEditor
                  value={classicForm.sample_answer}
                  onChange={(value) =>
                    setClassicForm({
                      ...classicForm,
                      sample_answer: value,
                    })
                  }
                  rows={10}
                />
              </div>
            </div>
            <div className="sidebar-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowClassicModal(false);
                  setEditingQuestion(null);
                  resetClassicForm();
                }}
              >
                取消
              </button>
              {editingQuestion ? (
                <button
                  className="btn btn-primary"
                  onClick={handleUpdateClassicQuestion}
                  disabled={!classicForm.question.trim()}
                >
                  <span>更新</span>
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={handleCreateClassicQuestion}
                  disabled={!classicForm.question.trim()}
                >
                  <span>创建</span>
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* 创建/编辑侧边栏面板 */}
      {showCreateModal && (
        <>
          <div className="sidebar-overlay" onClick={() => {
            setShowCreateModal(false);
            setEditingNews(null);
            resetForm();
          }}></div>
          <div className="sidebar-panel">
            <div className="sidebar-header">
              <h3>{editingNews ? '编辑动态' : '创建动态'}</h3>
              <button
                className="sidebar-close"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingNews(null);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>
            <div className="sidebar-body">
              <div className="form-group">
                <label>标题 <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="请输入动态标题"
                />
              </div>
              <div className="form-group">
                <label>内容</label>
                <MarkdownEditor
                  value={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                  rows={20}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>分类</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="industry_trend">行业趋势</option>
                    <option value="job_market">就业市场</option>
                    <option value="salary_info">薪资信息</option>
                    <option value="company_news">公司动态</option>
                    <option value="skill_trend">技能趋势</option>
                    <option value="career_tips">职业建议</option>
                    <option value="other">其他</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>状态</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="draft">草稿</option>
                    <option value="published">已发布</option>
                    <option value="archived">已归档</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  />
                  置顶显示
                </label>
              </div>
            </div>
            <div className="sidebar-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingNews(null);
                  resetForm();
                }}
              >
                取消
              </button>
              {editingNews ? (
                <button
                  className="btn btn-primary"
                  onClick={handleUpdateNews}
                  disabled={!formData.title.trim() || !formData.content}
                >
                  <span>更新</span>
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={handleCreateNews}
                  disabled={!formData.title.trim() || !formData.content}
                >
                  <span>创建</span>
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* 用户操作弹窗 */}
      {showUserModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{actionType === 'blacklist' ? '拉黑用户' : '冻结用户'}</h3>
              <button
                className="modal-close"
                onClick={() => setShowUserModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="user-info-box" style={{ flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                <div>
                  <div className="user-name-large">{selectedUser?.nickname}</div>
                  <div className="user-id-text">ID: {selectedUser?.user_id}</div>
                  <div className="user-contact-info">
                    {selectedUser?.email} | {selectedUser?.phone}
                  </div>
                </div>
              </div>

              {actionType === 'freeze' && (
                <div className="form-group">
                  <label>冻结天数</label>
                  <select
                    value={freezeDays}
                    onChange={(e) => setFreezeDays(parseInt(e.target.value))}
                  >
                    <option value="1">1天</option>
                    <option value="3">3天</option>
                    <option value="7">7天</option>
                    <option value="15">15天</option>
                    <option value="30">30天</option>
                    <option value="90">90天</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>{actionType === 'blacklist' ? '拉黑原因' : '冻结原因'} <span className="required">*</span></label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder={actionType === 'blacklist' ? '请输入拉黑原因（将通过邮件通知用户）' : '请输入冻结原因（将通过邮件通知用户）'}
                  rows="4"
                />
              </div>

              <div className="warning-box">
                <strong>注意：</strong>
                {actionType === 'blacklist' ? (
                  <p>拉黑后，该用户将无法登录系统，无法使用任何功能。这是软删除操作，可以恢复。系统将自动发送邮件通知用户。</p>
                ) : (
                  <p>冻结后，该用户在冻结期间将无法登录系统。冻结到期后自动恢复。系统将自动发送邮件通知用户。</p>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowUserModal(false)}
              >
                取消
              </button>
              <button
                className={`btn ${actionType === 'blacklist' ? 'btn-danger' : 'btn-warning'}`}
                onClick={handleUserAction}
                disabled={!actionReason.trim()}
              >
                {actionType === 'blacklist' ? '确认拉黑' : '确认冻结'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUpgradeModal && (
        <div className="modal-overlay op-modal-overlay" onClick={closeUpgradeModal}>
          <div className="modal-content upgrade-member-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header upgrade-modal-header">
              <div>
                <h3>{upgradeModalStep === 'form' ? '升级会员' : '确认升级'}</h3>
                <p className="upgrade-modal-subtitle">
                  {upgradeModalStep === 'form'
                    ? '填写目标等级与操作口令，进入核对后再执行。'
                    : '请核对以下信息，确认后将立即生效。'}
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={closeUpgradeModal}
                aria-label="关闭"
              >
                ×
              </button>
            </div>

            {upgradeModalStep === 'form' ? (
              <>
                <div className="modal-body">
                  <div className="upgrade-user-card">
                    <div className="upgrade-user-avatar" aria-hidden>
                      {(selectedUser?.nickname || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="upgrade-user-meta">
                      <div className="user-name-large">{selectedUser?.nickname}</div>
                      <div className="user-id-text">ID: {selectedUser?.user_id}</div>
                      <div className="upgrade-level-pill">
                        当前：<span>{selectedUser?.level}</span>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>目标会员等级</label>
                    <select
                      value={upgradeTargetLevel}
                      onChange={(e) => setUpgradeTargetLevel(e.target.value)}
                    >
                      <option value="L1">L1 会员（周卡）</option>
                      <option value="L2">L2 会员（月卡）</option>
                      <option value="L3">L3 会员（季卡）</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>
                      升级原因 <span className="required">*</span>
                    </label>
                    <textarea
                      value={upgradeReason}
                      onChange={(e) => setUpgradeReason(e.target.value)}
                      placeholder="必填，例如：用户已线下付款、活动赠送等"
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label>操作口令 <span className="required">*</span></label>
                    <input
                      type="password"
                      value={upgradeOperationPassword}
                      onChange={(e) => setUpgradeOperationPassword(e.target.value)}
                      placeholder="请输入操作口令"
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className="modal-footer upgrade-modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeUpgradeModal}>
                    取消
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleUpgradeGoToConfirm}
                    disabled={!upgradeReason.trim() || !upgradeOperationPassword.trim()}
                  >
                    下一步：核对信息
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-body upgrade-confirm-body">
                  <div className="upgrade-confirm-hero">
                    <div className="upgrade-confirm-icon-wrap" aria-hidden>
                      <span className="upgrade-confirm-icon">↑</span>
                    </div>
                    <p className="upgrade-confirm-lead">即将为该用户提升会员等级</p>
                  </div>
                  <dl className="upgrade-confirm-summary">
                    <div className="upgrade-confirm-row">
                      <dt>用户</dt>
                      <dd title={selectedUser?.nickname}>{selectedUser?.nickname}</dd>
                    </div>
                    <div className="upgrade-confirm-row">
                      <dt>当前等级</dt>
                      <dd>
                        <span className="upgrade-badge upgrade-badge-muted">{selectedUser?.level}</span>
                      </dd>
                    </div>
                    <div className="upgrade-confirm-row upgrade-confirm-row-highlight">
                      <dt>目标等级</dt>
                      <dd>
                        <span className="upgrade-badge upgrade-badge-target">{upgradeTargetLevel}</span>
                      </dd>
                    </div>
                    <div className="upgrade-confirm-row upgrade-confirm-row-block">
                      <dt>升级原因</dt>
                      <dd className="upgrade-reason-text">{upgradeReason.trim()}</dd>
                    </div>
                    <div className="upgrade-confirm-row">
                      <dt>预计到期</dt>
                      <dd>{getEstimatedUpgradeExpiryText(upgradeTargetLevel)}</dd>
                    </div>
                  </dl>
                  <p className="upgrade-confirm-hint">操作将写入后台记录，请确保与线下约定一致。</p>
                </div>
                <div className="modal-footer upgrade-modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setUpgradeModalStep('form')}
                    disabled={upgradeSubmitting}
                  >
                    返回修改
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleUpgradeExecute}
                    disabled={upgradeSubmitting}
                  >
                    {upgradeSubmitting ? '提交中…' : '确认执行升级'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 积分调整弹窗 */}
      {showPointsModal && (
        <div className="modal-overlay op-modal-overlay" onClick={closePointsModal}>
          <div className="modal-content upgrade-member-modal points-adjust-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header upgrade-modal-header">
              <div>
                <h3>{pointsModalStep === 'form' ? '调整用户积分' : '确认调整积分'}</h3>
                <p className="upgrade-modal-subtitle">
                  {pointsModalStep === 'form'
                    ? '选择场景或输入变化值，填写原因与口令后进入核对。'
                    : '请核对以下信息，确认后将立即生效并留痕。'}
                </p>
              </div>
              <button type="button" className="modal-close" onClick={closePointsModal} aria-label="关闭">
                ×
              </button>
            </div>

            {pointsModalStep === 'form' ? (
              <>
                <div className="modal-body points-adjust-body">
                  <div className="upgrade-user-card points-adjust-user-card">
                    <div className="upgrade-user-avatar" aria-hidden>
                      {(selectedUser?.nickname || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="upgrade-user-meta">
                      <div className="user-name-large">{selectedUser?.nickname}</div>
                      <div className="user-id-text">ID: {selectedUser?.user_id}</div>
                      <div className="points-adjust-current">
                        当前积分 <span className="points-adjust-current-num">{selectedUser?.point ?? 0}</span>
                      </div>
                    </div>
                  </div>

                  <div className="points-adjust-compact-grid">
                    <div className="points-adjust-col">
                      <label className="points-adjust-label">
                        积分变化 <span className="required">*</span>
                      </label>
                      <div className="points-delta-row">
                        <input
                          type="number"
                          className="points-delta-input"
                          value={pointsChange}
                          onChange={(e) => {
                            const parsed = parseInt(e.target.value, 10);
                            if (!Number.isFinite(parsed)) {
                              setPointsChange(0);
                              return;
                            }
                            setPointsChange(parsed > 100 ? 100 : parsed);
                          }}
                          placeholder="±数值"
                        />
                        <div className="points-quick-delta" role="group" aria-label="快捷调整">
                          <button type="button" className="points-quick-delta-btn points-quick-delta-up" onClick={() => setPointsChange(50)}>
                            +50
                          </button>
                          <button type="button" className="points-quick-delta-btn points-quick-delta-up" onClick={() => setPointsChange(100)}>
                            +100
                          </button>
                          <button type="button" className="points-quick-delta-btn points-quick-delta-down" onClick={() => setPointsChange(-50)}>
                            −50
                          </button>
                          <button type="button" className="points-quick-delta-btn points-quick-delta-down" onClick={() => setPointsChange(-100)}>
                            −100
                          </button>
                        </div>
                      </div>
                      <div
                        className={`points-after-preview ${pointsChange > 0 ? 'is-up' : pointsChange < 0 ? 'is-down' : ''}`}
                      >
                        调整后 <strong>{Math.max(0, (selectedUser?.point || 0) + pointsChange)}</strong>
                        {pointsChange !== 0 && (
                          <span className="points-after-delta">
                            （{pointsChange > 0 ? '+' : ''}
                            {pointsChange}）
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="points-adjust-col">
                      <label className="points-adjust-label">场景预设</label>
                      <div className="points-scenario-chips">
                        <button
                          type="button"
                          className={`points-scenario-chip ${pointsTemplate === 'compensation' ? 'active' : ''}`}
                          onClick={() => applyPointsTemplate('compensation')}
                        >
                          系统补偿 +100
                        </button>
                        <button
                          type="button"
                          className={`points-scenario-chip ${pointsTemplate === 'reward' ? 'active' : ''}`}
                          onClick={() => applyPointsTemplate('reward')}
                        >
                          活动奖励 +100
                        </button>
                        <button
                          type="button"
                          className={`points-scenario-chip ${pointsTemplate === 'penalty' ? 'active' : ''}`}
                          onClick={() => applyPointsTemplate('penalty')}
                        >
                          违规处罚 −50
                        </button>
                        <button
                          type="button"
                          className={`points-scenario-chip ${pointsTemplate === 'refund' ? 'active' : ''}`}
                          onClick={() => applyPointsTemplate('refund')}
                        >
                          退款补偿 +50
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="form-group points-adjust-reason">
                    <label>
                      调整原因 <span className="required">*</span>
                    </label>
                    <textarea
                      value={pointsReason}
                      onChange={(e) => setPointsReason(e.target.value)}
                      placeholder="必填，用于后台追溯"
                      rows={2}
                    />
                  </div>
                  <div className="form-group points-adjust-password">
                    <label>
                      操作口令 <span className="required">*</span>
                    </label>
                    <input
                      type="password"
                      value={pointsOperationPassword}
                      onChange={(e) => setPointsOperationPassword(e.target.value)}
                      placeholder="请输入操作口令"
                      autoComplete="off"
                    />
                  </div>
                  <p className="points-adjust-footnote">操作将记录操作人、时间与原因；积分最低为 0。</p>
                </div>
                <div className="modal-footer upgrade-modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closePointsModal}>
                    取消
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handlePointsGoToConfirm}
                    disabled={pointsChange === 0 || !pointsReason.trim() || !pointsOperationPassword.trim()}
                  >
                    下一步：核对信息
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-body upgrade-confirm-body points-confirm-body">
                  <div className="upgrade-confirm-hero">
                    <div className="upgrade-confirm-icon-wrap points-confirm-icon-wrap" aria-hidden>
                      <span className="upgrade-confirm-icon points-confirm-icon">±</span>
                    </div>
                    <p className="upgrade-confirm-lead">即将调整该用户积分余额</p>
                  </div>
                  <dl className="upgrade-confirm-summary points-confirm-summary">
                    <div className="upgrade-confirm-row">
                      <dt>用户</dt>
                      <dd title={selectedUser?.nickname}>{selectedUser?.nickname}</dd>
                    </div>
                    <div className="upgrade-confirm-row">
                      <dt>当前积分</dt>
                      <dd>{selectedUser?.point ?? 0}</dd>
                    </div>
                    <div className="upgrade-confirm-row">
                      <dt>变化</dt>
                      <dd>
                        <span className={pointsChange >= 0 ? 'points-change-up' : 'points-change-down'}>
                          {pointsChange > 0 ? '+' : ''}
                          {pointsChange}
                        </span>
                      </dd>
                    </div>
                    <div className="upgrade-confirm-row upgrade-confirm-row-highlight">
                      <dt>调整后</dt>
                      <dd>
                        <span className="upgrade-badge upgrade-badge-target">
                          {Math.max(0, (selectedUser?.point || 0) + pointsChange)}
                        </span>
                      </dd>
                    </div>
                    <div className="upgrade-confirm-row upgrade-confirm-row-block">
                      <dt>原因</dt>
                      <dd className="upgrade-reason-text">{pointsReason.trim()}</dd>
                    </div>
                  </dl>
                  <p className="upgrade-confirm-hint">确认后不可撤销，请与线下约定一致。</p>
                </div>
                <div className="modal-footer upgrade-modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setPointsModalStep('form')}
                    disabled={pointsSubmitting}
                  >
                    返回修改
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handlePointsExecute}
                    disabled={pointsSubmitting}
                  >
                    {pointsSubmitting ? '提交中…' : '确认执行调整'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 用户详情弹窗 */}
      {showDetailsModal && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content user-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>用户详情</h3>
              <button className="close-btn" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {loadingDetails ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>加载中...</p>
                </div>
              ) : userDetails ? (
                <div className="user-details-content">
                  {/* 用户基本信息 */}
                  <div className="detail-section">
                    <h4>基本信息</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>用户ID:</label>
                        <span>{userDetails.user_info.user_id}</span>
                      </div>
                      <div className="detail-item">
                        <label>昵称:</label>
                        <span>{userDetails.user_info.nickname}</span>
                      </div>
                      <div className="detail-item">
                        <label>邮箱:</label>
                        <span>{userDetails.user_info.email || '-'}</span>
                      </div>
                      <div className="detail-item">
                        <label>手机号:</label>
                        <span>{userDetails.user_info.phone || '-'}</span>
                      </div>
                      <div className="detail-item">
                        <label>等级:</label>
                        <span>{userDetails.user_info.level}</span>
                      </div>
                      <div className="detail-item">
                        <label>角色:</label>
                        <span>{userDetails.user_info.role}</span>
                      </div>
                      <div className="detail-item">
                        <label>注册时间:</label>
                        <span>{new Date(userDetails.user_info.created_at).toLocaleString()}</span>
                      </div>
                      <div className="detail-item">
                        <label>最后更新:</label>
                        <span>{new Date(userDetails.user_info.updated_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* 统计数据 */}
                  <div className="detail-section">
                    <h4>统计数据</h4>
                    <div className="stats-grid">
                      <div className="stat-box">
                        <div className="stat-label">累计充值</div>
                        <div className="stat-value">¥{userDetails.statistics.total_recharge.toFixed(2)}</div>
                      </div>
                      <div className="stat-box">
                        <div className="stat-label">累计获得积分</div>
                        <div className="stat-value">+{userDetails.statistics.total_points_earned}</div>
                      </div>
                      <div className="stat-box">
                        <div className="stat-label">累计消费积分</div>
                        <div className="stat-value">-{userDetails.statistics.total_points_spent}</div>
                      </div>
                      <div className="stat-box">
                        <div className="stat-label">当前积分</div>
                        <div className="stat-value">{userDetails.statistics.current_points}</div>
                      </div>
                    </div>
                  </div>

                  {/* 功能使用情况 */}
                  {userDetails.usage_statistics && (
                    <div className="detail-section">
                      <h4>功能使用情况</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        {(() => {
                          const m = userDetails.usage_statistics.this_month || {};
                          const items = [
                            { name: '职位搜索', value: m.job_search || 0, color: '#6B8AFF' },
                            { name: '简历诊断', value: m.resume_diagnosis || 0, color: '#9B59B6' },
                            { name: '面试押题', value: m.interview_prep || 0, color: '#FF4D79' },
                            { name: '模拟面试', value: m.mock_interview || 0, color: '#FF8C2F' },
                            { name: '会议助手', value: m.meeting_assistant || 0, color: '#00B5D8' },
                          ];
                          const total = items.reduce((s, i) => s + i.value, 0);
                          return (
                            <div style={{ width: '100%', height: 180, paddingTop: 12, paddingBottom: 12 }}>
                              {total === 0 ? (
                                <p className="no-data">暂无数据</p>
                              ) : (
                                <ResponsiveContainer>
                                  <PieChart margin={{ top: 16, right: 8, bottom: 18, left: 8 }}>
                                    <Pie data={items} dataKey="value" nameKey="name" innerRadius={50} outerRadius={70} paddingAngle={2} cx="50%" cy="50%" legendType="circle">
                                      {items.map((e, i) => <Cell key={i} fill={e.color} />)}
                                    </Pie>
                                    <Tooltip formatter={(v, n) => [`${v}`, n]} />
                                  </PieChart>
                                </ResponsiveContainer>
                              )}
                              <div style={{ textAlign: 'center', marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                                本月 {m.month || ''}
                              </div>
                            </div>
                          );
                        })()}
                        {(() => {
                          const t = userDetails.usage_statistics.total || {};
                          const items = [
                            { name: '职位搜索', value: t.job_search || 0, color: '#6B8AFF' },
                            { name: '简历诊断', value: t.resume_diagnosis || 0, color: '#9B59B6' },
                            { name: '面试押题', value: t.interview_prep || 0, color: '#FF4D79' },
                            { name: '模拟面试', value: t.mock_interview || 0, color: '#FF8C2F' },
                            { name: '会议助手', value: t.meeting_assistant || 0, color: '#00B5D8' },
                          ];
                          const total = items.reduce((s, i) => s + i.value, 0);
                          return (
                            <div style={{ width: '100%', height: 180, paddingTop: 12, paddingBottom: 12 }}>
                              {total === 0 ? (
                                <p className="no-data">暂无数据</p>
                              ) : (
                                <ResponsiveContainer>
                                  <PieChart margin={{ top: 16, right: 8, bottom: 18, left: 8 }}>
                                    <Pie data={items} dataKey="value" nameKey="name" innerRadius={50} outerRadius={70} paddingAngle={2} cx="50%" cy="50%" legendType="circle">
                                      {items.map((e, i) => <Cell key={i} fill={e.color} />)}
                                    </Pie>
                                    <Tooltip formatter={(v, n) => [`${v}`, n]} />
                                  </PieChart>
                                </ResponsiveContainer>
                              )}
                              <div style={{ textAlign: 'center', marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                                累计
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* 功能费用明细 */}
                  {userDetails.feature_costs && (
                    <div className="detail-section">
                      <h4>功能费用明细</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>本月费用（{userDetails.feature_costs.currency}）</div>
                          {(userDetails.feature_costs.this_month && userDetails.feature_costs.this_month.length > 0) ? (
                            <table className="records-table">
                              <thead>
                                <tr>
                                  <th>功能</th>
                                  <th>费用</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userDetails.feature_costs.this_month.map((item, idx) => (
                                  <tr key={idx}>
                                    <td>{item.feature_name}</td>
                                    <td>¥{Number(item.cost || 0).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="no-data">暂无数据</p>
                          )}
                        </div>
                        <div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>累计费用（{userDetails.feature_costs.currency}）</div>
                          {(userDetails.feature_costs.total && userDetails.feature_costs.total.length > 0) ? (
                            <table className="records-table">
                              <thead>
                                <tr>
                                  <th>功能</th>
                                  <th>费用</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userDetails.feature_costs.total.map((item, idx) => (
                                  <tr key={idx}>
                                    <td>{item.feature_name}</td>
                                    <td>¥{Number(item.cost || 0).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="no-data">暂无数据</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {userDetails.resume_generation_statistics && (
                    <div className="detail-section">
                      <h4>简历生成功能使用</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>本月</div>
                          <table className="records-table">
                            <tbody>
                              <tr>
                                <td>AI一键生成简历</td>
                                <td>{userDetails.resume_generation_statistics.this_month.resume_generation || 0} 次</td>
                              </tr>
                              <tr>
                                <td>简历润色</td>
                                <td>{userDetails.resume_generation_statistics.this_month.resume_assist_write || 0} 次</td>
                              </tr>
                              <tr>
                                <td>简历导出</td>
                                <td>{userDetails.resume_generation_statistics.this_month.resume_pdf_export || 0} 次</td>
                              </tr>
                              <tr>
                                <td>语音输入</td>
                                <td>{userDetails.resume_generation_statistics.this_month.resume_voice_input || 0} 次</td>
                              </tr>
                              <tr>
                                <td>导出积分消耗</td>
                                <td>{userDetails.resume_generation_statistics.this_month.resume_export_points_spent || 0} 分</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>累计</div>
                          <table className="records-table">
                            <tbody>
                              <tr>
                                <td>AI一键生成简历</td>
                                <td>{userDetails.resume_generation_statistics.total.resume_generation || 0} 次</td>
                              </tr>
                              <tr>
                                <td>简历润色</td>
                                <td>{userDetails.resume_generation_statistics.total.resume_assist_write || 0} 次</td>
                              </tr>
                              <tr>
                                <td>简历导出</td>
                                <td>{userDetails.resume_generation_statistics.total.resume_pdf_export || 0} 次</td>
                              </tr>
                              <tr>
                                <td>语音输入</td>
                                <td>{userDetails.resume_generation_statistics.total.resume_voice_input || 0} 次</td>
                              </tr>
                              <tr>
                                <td>导出积分消耗</td>
                                <td>{userDetails.resume_generation_statistics.total.resume_export_points_spent || 0} 分</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 充值记录 */}
                  <div className="detail-section">
                    <h4>充值记录</h4>
                    {userDetails.recharge_records.length > 0 ? (
                      <div className="records-list">
                        <table className="records-table">
                          <thead>
                            <tr>
                              <th>订单号</th>
                              <th>金额</th>
                              <th>状态</th>
                              <th>支付方式</th>
                              <th>创建时间</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userDetails.recharge_records.map(record => (
                              <tr key={record.id}>
                                <td>{record.order_no}</td>
                                <td>¥{record.amount.toFixed(2)}</td>
                                <td>
                                  <span className={`status-badge status-${record.status}`}>
                                    {record.status === 'paid' ? '已支付' :
                                      record.status === 'pending' ? '待支付' :
                                        record.status === 'failed' ? '支付失败' :
                                          record.status === 'refunded' ? '已退款' : record.status}
                                  </span>
                                </td>
                                <td>{record.payment_method === 'alipay' ? '支付宝' :
                                  record.payment_method === 'wechat' ? '微信' :
                                    record.payment_method === 'unionpay' ? '银联' : record.payment_method}</td>
                                <td>{new Date(record.created_at).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="no-data">暂无充值记录</p>
                    )}
                  </div>

                  {/* 积分变化记录 */}
                  <div className="detail-section">
                    <h4>积分变化记录</h4>
                    {userDetails.points_history.length > 0 ? (
                      <div className="records-list">
                        <table className="records-table">
                          <thead>
                            <tr>
                              <th>时间</th>
                              <th>变化</th>
                              <th>原因</th>
                              <th>备注</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userDetails.points_history.map(record => (
                              <tr key={record.id}>
                                <td>{new Date(record.created_at).toLocaleString()}</td>
                                <td>
                                  <span className={record.change > 0 ? 'points-earned' : 'points-spent'}>
                                    {record.change > 0 ? '+' : ''}{record.change}
                                  </span>
                                </td>
                                <td>{record.reason}</td>
                                <td>{record.meta || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="no-data">暂无积分变化记录</p>
                    )}
                  </div>
                </div>
              ) : (
                <p>加载失败</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationDashboard;
